import type { ItemModifiers, ItemVfxConfig } from '../types/item';
import { ItemType, WeaponSubtype, ArmorSubtype } from '../types/item';
import { ItemRarity } from '../constants';
import type { ToolType } from './tools';
import { GEMS } from './gems';
import { FISH_SPECIES } from './fish';

/**
 * Definición completa de un item template.
 * Esta es la fuente de verdad única para todos los items del juego.
 * Los templates ya NO se almacenan en la base de datos.
 *
 * NOTA sobre rareza: el template NO tiene un campo `rarity`. La rareza efectiva
 * de cada instancia se determina al crear el drop así:
 *  - `unique: true`        → LEGENDARY (mods fijos del template).
 *  - `setId` definido      → SET (mods fijos + bonos por piezas equipadas).
 *  - `affixable: true`     → se rolea COMMON/RARE/EPIC (90/9/1) y se rolean afijos.
 *  - ninguno de los tres   → COMMON sin afijos (pociones, materiales, etc.).
 */
export interface ItemDefinition {
  id: string;
  name: string;
  sellable: boolean;
  icon: string;
  animation?: string;
  /**
   * Tipo de item (equipment, consumable, quest, material)
   */
  type?: string;
  /**
   * Subtipo del item (ej: para weapons: dagger, sword, staff, etc.)
   */
  subtype?: string;
  slot?: string; // Slot de equipamiento (ej: 'weapon', 'body', 'shield', 'boots')
  /**
   * Si es true, el item es único y no se puede apilar (cada unidad ocupa 1 slot).
   * Marca el item como rareza LEGENDARY (modificadores fijos del template).
   */
  unique?: boolean;
  /**
   * Si es true, este template puede dropear con rareza COMMON/RARE/EPIC y
   * rolear afijos aleatorios al crear instancia. Los items affixable son
   * inherentemente no-apilables (cada instancia tiene rolls únicos).
   * Ignorado si el template tiene `unique` o `setId`.
   */
  affixable?: boolean;
  /** Flavor text / descripción. Se renderiza en el tooltip si está presente. */
  description?: string;
  /**
   * Género/número gramatical del nombre base, para concordar los prefijos adjetivos
   * del nombre DERIVADO de afijos (ej. "Espada Sanguinaria" vs "Báculo Sanguinario").
   * Opcional: si no se declara, se infiere del sustantivo cabecera del `name`
   * (`inferGrammar`). Tagear sólo las bases donde la heurística falle. Ver
   * docs/ITEM_NAMING.md.
   */
  grammar?: { gender: 'm' | 'f'; plural?: boolean };
  /** Empuñadura del arma (solo aplica a items con slot='weapon'). */
  weaponHand?: 'one' | 'two';
  // ─── Radio de ataque (solo armas, slot='weapon') ──────────────────────────
  // Forma base del golpe melee de esta arma. Son intrínsecos (no se suman entre
  // items); los bonos vienen de los mods attackRange* en modifiers. Ver
  // docs/COMBAT_SYSTEMS.md. Sin estos campos, el arma usa la base unarmed.
  /** Ancho del golpe, SIEMPRE impar ≥1 (1 = solo central, 3 = +1 a cada lado). */
  attackWidth?: number;
  /** Largo del golpe hacia adelante, 1..5. */
  attackLength?: number;
  /** Tiles hacia atrás del golpe, 0 o 1. */
  attackBack?: number;
  /** Stamina consumida por golpe con esta arma (fallback: base unarmed). */
  attackStaminaCost?: number;
  /** Clases que pueden equipar el item. Vacío/undefined = cualquier clase. */
  requiredClasses?: string[];
  /** Facción requerida para equipar/usar. undefined = cualquiera. Ver docs/FACTIONS.md. */
  requiredFaction?: string;
  maxStack?: number;
  // IDs de sprites visuales (para equipamiento)
  bodyId?: number;
  weaponId?: number;
  shieldId?: number;
  headId?: number;
  helmId?: number;
  bootId?: number;
  modifiers: ItemModifiers;
  setId?: string; // ID del set al que pertenece este item
  buyPrice?: number;
  sellPrice?: number;
  /**
   * Precio de venta PERSONALIZADO (opt-out de la regla global venta = 30% de compra).
   * Por defecto la venta se deriva del `buyPrice` (getSellPrice); marcá esto en items que
   * necesiten compra Y venta a mano (típicamente ÚNICOS) para que su `sellPrice` explícito
   * NO se pise por la normalización. Ver §normalización de precios al final del archivo.
   */
  keepSellPrice?: boolean;
  /**
   * Nivel de Herrería (Smithing) requerido para forjar este item, estilo OSRS.
   * METADATA por ahora: el sistema de skills todavía no existe, así que NO gatea
   * equip ni compra — sólo se muestra en el tooltip para dar la "curva" de
   * progresión. Números copiados de OSRS (ver docs/SMITHING_TIERS.md).
   */
  requiredSmithingLevel?: number;
  /**
   * Tier de material del ladder de la "Colección Inicial" (estilo OSRS):
   * 'bronze' | 'iron' | 'steel' | 'mithril' | 'adamant' | 'rune' | 'dragon'.
   * Sirve para agrupar en UI/wiki y para el futuro sistema de skills.
   */
  materialTier?: string;

  // ─── HERRAMIENTAS de skill (minería / leñería) — ver docs/MINING_AND_SMITHING.md ─
  /**
   * Marca el item como HERRAMIENTA de extracción. Un pico permite minar; un
   * hacha de leñador permite talar. La herramienta funciona esté equipada
   * (slot weapon) o guardada en el inventario. Sólo las herramientas se rompen.
   */
  toolType?: ToolType;
  /**
   * Tier de material de la herramienta (misma escala que materialTier):
   * 'bronze' | 'iron' | 'steel' | 'mithril' | 'adamant' | 'rune'. Determina qué
   * tiers de recurso puede extraer (regla: pico de tier ≥ tier_del_recurso − 1).
   */
  toolTier?: string;
  /**
   * Probabilidad (0..1) de que la herramienta se ROMPA en cada extracción. Muy
   * baja y menor cuanto mejor el material. Al romperse, la instancia queda
   * `broken` y hay que repararla con el Maestro Herrero.
   */
  toolBreakChance?: number;
  /** Nivel de Minería requerido para USAR esta herramienta / extraer este recurso. */
  requiredMiningLevel?: number;
  /** Nivel de Leñería requerido para USAR esta herramienta / talar este recurso. */
  requiredWoodcuttingLevel?: number;
  /**
   * Nivel de Sastrería requerido para CONFECCIONAR esta prenda de tela (túnicas /
   * armaduras de tela). METADATA por ahora (no gatea equip), igual que
   * `requiredSmithingLevel`. Ver docs/SKILLS_SYSTEM.md.
   */
  requiredTailoringLevel?: number;

  /**
   * Efectos visuales de partículas asociados al item (ej. FX al equiparlo).
   * Los `fxId` apuntan al catálogo `shared/data/particleEffects.json`.
   */
  vfx?: ItemVfxConfig;

  /**
   * Hechizo (`shared/data/spells.ts`) que este item OTORGA mientras esté EQUIPADO: al
   * equipar se aprende + entra en la hotbar, al desequipar se olvida + sale de la
   * hotbar. El hechizo debe ser `grantOnly`. Ver docs/SPELLS.md.
   */
  grantsSpellId?: string;

  // ─── INVOCACIONES por item (ver docs/SUMMONS.md) ─────────────────────────
  /** Perfil de summon que este item INVOCA mientras esté equipado (aura toggle atada al item). */
  grantsSummonProfileId?: string;
  /** Perfil de summon que este item invoca al USARLO (tecla Usar / click "Usar"). */
  useSummonProfileId?: string;
  /** Cooldown (ms) entre usos de `useSummonProfileId`. Default 0. */
  useCooldownMs?: number;
  /** Si true, usar el item consume 1 unidad del stack (consumible). */
  consumeOnUse?: boolean;

  /**
   * POCIÓN DE REVELADO — al USAR el item, revela (para todos) a las entidades
   * INVISIBLES en un cuadrado de `revealInvisibilityRadius` tiles de lado centrado
   * en el usuario (9 = 9×9). Consume 1 unidad (con `consumeOnUse`) y respeta
   * `useCooldownMs`. Lo maneja EquipmentRoomHandlers.handleUseItemMessage. Ver
   * docs/INVISIBILITY.md.
   */
  revealInvisibilityRadius?: number;

  /**
   * COMIDA — al USAR (comer) aplica un `TemporaryBuff` "Bien Alimentado" con estos
   * `modifiers` por `durationMs`. Uno activo por vez (refresh: comer de nuevo reemplaza
   * el previo). Se consume siempre 1 unidad. El buff NO cura al instante (lleva tasas:
   * regen, skillXpBonus, toolBreakReduction, magicFindVsKingdom, etc.). Lo maneja
   * EquipmentRoomHandlers.handleUseItemMessage. Ver docs/FISHING_SKILL.md / plan de Comida.
   */
  foodBuff?: {
    /** Nombre para el HUD del buff (ej. "Bien Alimentado: Salmón asado"). */
    name: string;
    /** Modificadores que aplica mientras dura. */
    modifiers: ItemModifiers;
    /** Duración del buff en ms. */
    durationMs: number;
  };
}

/** Ratio global venta/compra: se vende por el 30% de lo que cuesta comprar. */
export const SELL_PRICE_RATIO = 0.3;

/**
 * Helper para obtener el sellPrice efectivo de un item: respeta el override
 * explícito o aplica el default (30% del buyPrice, `SELL_PRICE_RATIO`). Devuelve 0
 * si no se puede vender (sellable=false o sin precios).
 */
export function getEffectiveSellPrice(item: ItemDefinition): number {
  if (item.sellable === false) return 0;
  if (item.sellPrice !== undefined) return Math.max(0, Math.floor(item.sellPrice));
  if (item.buyPrice !== undefined) return Math.max(0, Math.round(item.buyPrice * SELL_PRICE_RATIO));
  return 0;
}

/**
 * Índice completo de items por id.
 * Fuente única de verdad para los templates del juego.
 *
 * Catálogo wipeado el 2026-05-25 — ver docs/ITEMS_LEGACY_SNAPSHOT.md para el
 * contenido anterior (armaduras, escudos, sets, pociones, etc.). Se reconstruye
 * de a poco a medida que carguemos assets nuevos.
 */
export const ITEMS: Record<string, ItemDefinition> = {
  // ===== ARMAS BÁSICAS (assets con convención `<subtipo>1`) =====
  weapon_sword_001: {
    id: 'weapon_sword_001',
    name: 'Espada de Bronce',
    sellable: true,
    icon: 'sword1',
    materialTier: 'bronze',
    requiredSmithingLevel: 4,
    type: ItemType.EQUIPMENT,
    subtype: WeaponSubtype.SWORD,
    slot: 'weapon',
    weaponId: 1,
    weaponHand: 'one',
    // Radio de ataque: 1×1 (un tile al frente), velocidad y stamina estándar.
    attackWidth: 1,
    attackLength: 1,
    attackStaminaCost: 5,
    modifiers: {
      minDamage: 5,
      maxDamage: 10,
    },
    buyPrice: 80,
    vfx: {
      equipped: [{ fxId: 'fx_weapon_ember_sparks', slot: 'weapon_tip' }],
    },
  },
  weapon_dagger_002: {
    id: 'weapon_dagger_002',
    name: 'Daga de Bronce',
    sellable: true,
    icon: 'dagger1',
    materialTier: 'bronze',
    requiredSmithingLevel: 1,
    type: ItemType.EQUIPMENT,
    subtype: WeaponSubtype.DAGGER,
    slot: 'weapon',
    weaponId: 5,
    weaponHand: 'one',
    // Daga: rápida + certera. Velocidad/crit intrínsecos del subtipo (ver weaponStats.ts).
    attackWidth: 1,
    attackLength: 1,
    attackStaminaCost: 3,
    modifiers: {
      minDamage: 3,
      maxDamage: 7,
    },
    buyPrice: 60,
  },
  weapon_axe_001: {
    id: 'weapon_axe_001',
    name: 'Hacha de Bronce',
    sellable: true,
    icon: 'axe1',
    materialTier: 'bronze',
    requiredSmithingLevel: 10,
    type: ItemType.EQUIPMENT,
    subtype: WeaponSubtype.AXE,
    slot: 'weapon',
    weaponId: 6,
    weaponHand: 'one',
    // Hacha: CLEAVE — ancho 3 (pega al frente + 1 tile a cada lado), lenta y pesada.
    // Velocidad intrínseca del subtipo (ver weaponStats.ts).
    attackWidth: 3,
    attackLength: 1,
    attackStaminaCost: 7,
    modifiers: {
      minDamage: 7,
      maxDamage: 12,
    },
    buyPrice: 100,
  },
  weapon_axe_002: {
    id: 'weapon_axe_002',
    name: 'Hacha de Guerra',
    sellable: true,
    icon: 'axe2',
    type: ItemType.EQUIPMENT,
    subtype: WeaponSubtype.AXE,
    slot: 'weapon',
    weaponId: 13,
    weaponHand: 'one',
    affixable: true,
    modifiers: {
      minDamage: 8,
      maxDamage: 14,
    },
    buyPrice: 120,
  },
  weapon_axe_003: {
    id: 'weapon_axe_003',
    name: 'Hacha Pesada',
    sellable: true,
    icon: 'axe3',
    type: ItemType.EQUIPMENT,
    subtype: WeaponSubtype.AXE,
    slot: 'weapon',
    weaponId: 14,
    weaponHand: 'one',
    // Hacha Pesada: CLEAVE ancho 3, alta stamina. Velocidad del subtipo (weaponStats.ts).
    attackWidth: 3,
    attackLength: 1,
    attackStaminaCost: 9,
    affixable: true,
    modifiers: {
      minDamage: 9,
      maxDamage: 15,
    },
    buyPrice: 130,
  },
  weapon_axe_004: {
    id: 'weapon_axe_004',
    name: 'Hacha Doble',
    sellable: true,
    icon: 'axe4',
    type: ItemType.EQUIPMENT,
    subtype: WeaponSubtype.AXE,
    slot: 'weapon',
    weaponId: 15,
    weaponHand: 'one',
    // Hacha Doble: barrido amplio — ancho 3 + 1 tile atrás (tipo molinete). Velocidad
    // del subtipo (weaponStats.ts).
    attackWidth: 3,
    attackLength: 1,
    attackBack: 1,
    attackStaminaCost: 10,
    affixable: true,
    modifiers: {
      minDamage: 11,
      maxDamage: 18,
    },
    buyPrice: 160,
  },
  weapon_mace_001: {
    id: 'weapon_mace_001',
    name: 'Maza',
    sellable: true,
    icon: 'mace1',
    type: ItemType.EQUIPMENT,
    subtype: WeaponSubtype.MACE,
    slot: 'weapon',
    weaponId: 18,
    weaponHand: 'one',
    affixable: true,
    modifiers: {
      minDamage: 6,
      maxDamage: 11,
    },
    buyPrice: 95,
  },
  weapon_mace_002: {
    id: 'weapon_mace_002',
    name: 'Maza de Hierro',
    sellable: true,
    icon: 'mace2',
    type: ItemType.EQUIPMENT,
    subtype: WeaponSubtype.MACE,
    slot: 'weapon',
    weaponId: 19,
    weaponHand: 'one',
    affixable: true,
    modifiers: {
      minDamage: 8,
      maxDamage: 13,
    },
    buyPrice: 115,
  },
  weapon_mace_003: {
    id: 'weapon_mace_003',
    name: 'Maza Pesada',
    sellable: true,
    icon: 'mace3',
    type: ItemType.EQUIPMENT,
    subtype: WeaponSubtype.MACE,
    slot: 'weapon',
    weaponId: 20,
    weaponHand: 'one',
    affixable: true,
    modifiers: {
      minDamage: 10,
      maxDamage: 16,
    },
    buyPrice: 135,
  },
  weapon_mace_004: {
    id: 'weapon_mace_004',
    name: 'Lucero del Alba',
    sellable: true,
    icon: 'mace4',
    type: ItemType.EQUIPMENT,
    subtype: WeaponSubtype.MACE,
    slot: 'weapon',
    weaponId: 21,
    weaponHand: 'one',
    // Lucero del Alba: golpe único 1×1 demoledor, pesado. Velocidad del subtipo (weaponStats.ts).
    attackWidth: 1,
    attackLength: 1,
    attackStaminaCost: 9,
    affixable: true,
    modifiers: {
      minDamage: 12,
      maxDamage: 19,
    },
    buyPrice: 165,
  },
  weapon_bow_001: {
    id: 'weapon_bow_001',
    name: 'Arco',
    sellable: true,
    icon: 'bow1',
    type: ItemType.EQUIPMENT,
    subtype: WeaponSubtype.BOW,
    slot: 'weapon',
    weaponId: 7,
    weaponHand: 'two',
    affixable: true,
    modifiers: {
      minDamage: 6,
      maxDamage: 11,
    },
    buyPrice: 110,
  },
  weapon_bow_002: {
    id: 'weapon_bow_002',
    name: 'Arco Largo',
    sellable: true,
    icon: 'bow2',
    type: ItemType.EQUIPMENT,
    subtype: WeaponSubtype.BOW,
    slot: 'weapon',
    weaponId: 16,
    weaponHand: 'two',
    affixable: true,
    modifiers: {
      minDamage: 7,
      maxDamage: 12,
    },
    buyPrice: 130,
  },
  weapon_bow_003: {
    id: 'weapon_bow_003',
    name: 'Arco Recurvo',
    sellable: true,
    icon: 'bow3',
    type: ItemType.EQUIPMENT,
    subtype: WeaponSubtype.BOW,
    slot: 'weapon',
    weaponId: 17,
    weaponHand: 'two',
    affixable: true,
    modifiers: {
      minDamage: 8,
      maxDamage: 13,
    },
    buyPrice: 150,
  },
  weapon_wand_001: {
    id: 'weapon_wand_001',
    name: 'Varita',
    sellable: true,
    icon: 'wand1',
    type: ItemType.EQUIPMENT,
    subtype: WeaponSubtype.WAND,
    slot: 'weapon',
    weaponId: 8,
    weaponHand: 'one',
    affixable: true,
    modifiers: {
      minDamage: 2,
      maxDamage: 5,
      mana: 15,
    },
    buyPrice: 70,
  },
  weapon_knuckles_001: {
    id: 'weapon_knuckles_001',
    name: 'Puños',
    sellable: true,
    icon: 'knuckles1',
    type: ItemType.EQUIPMENT,
    subtype: WeaponSubtype.KNUCKLES,
    slot: 'weapon',
    weaponId: 9,
    weaponHand: 'one',
    // Puños: el arma más rápida, stamina mínima, 1×1. Velocidad del subtipo (weaponStats.ts).
    attackWidth: 1,
    attackLength: 1,
    attackStaminaCost: 2,
    affixable: true,
    modifiers: {
      minDamage: 4,
      maxDamage: 8,
    },
    buyPrice: 75,
  },
  weapon_throwable_001: {
    id: 'weapon_throwable_001',
    name: 'Arma Arrojadiza',
    sellable: true,
    icon: 'throwable1',
    type: ItemType.EQUIPMENT,
    subtype: WeaponSubtype.THROWABLE,
    slot: 'weapon',
    weaponId: 10,
    weaponHand: 'one',
    affixable: true,
    modifiers: {
      minDamage: 4,
      maxDamage: 9,
    },
    buyPrice: 65,
  },
  weapon_staff_001: {
    id: 'weapon_staff_001',
    name: 'Báculo',
    sellable: true,
    icon: 'staff1',
    type: ItemType.EQUIPMENT,
    subtype: WeaponSubtype.STAFF,
    slot: 'weapon',
    weaponId: 11,
    weaponHand: 'two',
    // Báculo: alcance largo (largo 2 = pega 2 tiles al frente, tipo asta). Velocidad del
    // subtipo (weaponStats.ts).
    attackWidth: 1,
    attackLength: 2,
    attackStaminaCost: 4,
    affixable: true,
    modifiers: {
      minDamage: 3,
      maxDamage: 7,
      mana: 20,
    },
    buyPrice: 90,
  },
  weapon_crossbow_001: {
    id: 'weapon_crossbow_001',
    name: 'Ballesta',
    sellable: true,
    icon: 'crossbow1',
    type: ItemType.EQUIPMENT,
    subtype: WeaponSubtype.CROSSBOW,
    slot: 'weapon',
    weaponId: 12,
    weaponHand: 'two',
    affixable: true,
    modifiers: {
      minDamage: 8,
      maxDamage: 13,
    },
    buyPrice: 130,
  },
  weapon_crossbow_002: {
    id: 'weapon_crossbow_002',
    name: 'Ballesta Pesada',
    sellable: true,
    icon: 'crossbow2',
    type: ItemType.EQUIPMENT,
    subtype: WeaponSubtype.CROSSBOW,
    slot: 'weapon',
    weaponId: 22,
    weaponHand: 'two',
    affixable: true,
    modifiers: {
      minDamage: 12,
      maxDamage: 18,
    },
    buyPrice: 200,
  },

  // ===== ARMADURAS (slot 'body') =====
  // Gráfico (ícono) en assets/entities/body/<icon>.png; spritesheet de walk
  // (anim del body al vestirlo) en assets/entities/body/anims/body<bodyId>.png.
  armor_body_001: {
    id: 'armor_body_001',
    name: 'Armadura de Bronce',
    sellable: true,
    icon: 'body1',
    type: ItemType.EQUIPMENT,
    subtype: ArmorSubtype.HEAVY,
    slot: 'body',
    bodyId: 1,
    materialTier: 'bronze',
    requiredSmithingLevel: 11,
    modifiers: {
      physDef: 3,
    },
    buyPrice: 60,
  },
  armor_body_002: {
    id: 'armor_body_002',
    name: 'Vestimenta',
    sellable: true,
    icon: 'body2',
    type: ItemType.EQUIPMENT,
    subtype: ArmorSubtype.CLOTH,
    slot: 'body',
    bodyId: 2,
    affixable: true,
    modifiers: {
      physDef: 3,
    },
    buyPrice: 60,
  },
  armor_body_003: {
    id: 'armor_body_003',
    name: 'Vestimenta',
    sellable: true,
    icon: 'body3',
    type: ItemType.EQUIPMENT,
    subtype: ArmorSubtype.CLOTH,
    slot: 'body',
    bodyId: 3,
    affixable: true,
    modifiers: {
      physDef: 3,
    },
    buyPrice: 60,
  },

  // ===== COLECCIÓN INICIAL — Ladder estilo Smithing (OSRS) =====
  // 7 categorías (espada/daga/hacha/casco/escudo/armadura/botas) × 7 tiers
  // (bronce→dragón). El Bronce de espada/daga/hacha/armadura son los starters de
  // arriba (weapon_sword_001, weapon_dagger_002, weapon_axe_001, armor_body_001).
  // `requiredSmithingLevel` copia los números de smithing de OSRS (metadata, sin
  // enforcement todavía). Curva de stats = base bronce × factor de tier
  // [1, 1.35, 1.8, 2.5, 3.3, 4.2, 5.2]. Arte real sólo para el bronce (armas/body
  // usan su icon; casco/escudo/botas usan el sprite equipado helm1/shield1/boot1
  // pero SIN icono 32×32 → 'noicon'); el resto de tiers usa 'noicon' hasta backfill
  // y reusa el visual id del bronce (weaponId/bodyId/helmId/shieldId/bootId).
  //
  // IMPORTANTE (reglas de diseño):
  //  - NINGUNA pieza es `affixable` → SIEMPRE rareza COMMON (normal), sin afijos.
  //  - El tier DRAGÓN es el tope y NO es forjable: no lleva `requiredSmithingLevel`
  //    (se obtendrá por otra vía, no por Herrería). Bronce→Rúnico sí lo llevan.
  // Ver docs/SMITHING_TIERS.md.

  // ── ESPADA (SWORD) — OSRS Sword: 4/19/34/54/74/89 ──
  weapon_sword_iron: {
    id: 'weapon_sword_iron', name: 'Espada de Hierro', sellable: true, icon: 'sword2',
    type: ItemType.EQUIPMENT, subtype: WeaponSubtype.SWORD, slot: 'weapon', weaponId: 23,
    weaponHand: 'one', attackWidth: 1, attackLength: 1, attackStaminaCost: 5,
    materialTier: 'iron', requiredSmithingLevel: 19,
    modifiers: { minDamage: 7, maxDamage: 14 }, buyPrice: 108,
  },
  weapon_sword_steel: {
    id: 'weapon_sword_steel', name: 'Espada de Acero', sellable: true, icon: 'sword3',
    type: ItemType.EQUIPMENT, subtype: WeaponSubtype.SWORD, slot: 'weapon', weaponId: 24,
    weaponHand: 'one', attackWidth: 1, attackLength: 1, attackStaminaCost: 5,
    materialTier: 'steel', requiredSmithingLevel: 34,
    modifiers: { minDamage: 9, maxDamage: 18 }, buyPrice: 144,
  },
  weapon_sword_mithril: {
    id: 'weapon_sword_mithril', name: 'Espada de Mithril', sellable: true, icon: 'sword4',
    type: ItemType.EQUIPMENT, subtype: WeaponSubtype.SWORD, slot: 'weapon', weaponId: 25,
    weaponHand: 'one', attackWidth: 1, attackLength: 1, attackStaminaCost: 5,
    materialTier: 'mithril', requiredSmithingLevel: 54,
    modifiers: { minDamage: 13, maxDamage: 25 }, buyPrice: 200,
  },
  weapon_sword_adamant: {
    id: 'weapon_sword_adamant', name: 'Espada de Adamantita', sellable: true, icon: 'sword5',
    type: ItemType.EQUIPMENT, subtype: WeaponSubtype.SWORD, slot: 'weapon', weaponId: 26,
    weaponHand: 'one', attackWidth: 1, attackLength: 1, attackStaminaCost: 5,
    materialTier: 'adamant', requiredSmithingLevel: 74,
    modifiers: { minDamage: 17, maxDamage: 33 }, buyPrice: 264,
  },
  weapon_sword_rune: {
    id: 'weapon_sword_rune', name: 'Espada Rúnica', sellable: true, icon: 'sword6',
    type: ItemType.EQUIPMENT, subtype: WeaponSubtype.SWORD, slot: 'weapon', weaponId: 27,
    weaponHand: 'one', attackWidth: 1, attackLength: 1, attackStaminaCost: 5,
    materialTier: 'rune', requiredSmithingLevel: 89,
    modifiers: { minDamage: 21, maxDamage: 42 }, buyPrice: 336,
  },
  weapon_sword_obsidian: {
    id: 'weapon_sword_obsidian', name: 'Espada de Obsidiana', sellable: true, icon: 'sword7',
    type: ItemType.EQUIPMENT, subtype: WeaponSubtype.SWORD, slot: 'weapon', weaponId: 28,
    weaponHand: 'one', attackWidth: 1, attackLength: 1, attackStaminaCost: 5,
    materialTier: 'obsidian', requiredSmithingLevel: 95,
    modifiers: { minDamage: 23, maxDamage: 47 }, buyPrice: 376,
  },
  weapon_sword_dragon: {
    id: 'weapon_sword_dragon', name: 'Espada de Dragón', sellable: true, icon: 'sword8',
    type: ItemType.EQUIPMENT, subtype: WeaponSubtype.SWORD, slot: 'weapon', weaponId: 29,
    weaponHand: 'one', attackWidth: 1, attackLength: 1, attackStaminaCost: 5,
    materialTier: 'dragon',
    modifiers: { minDamage: 26, maxDamage: 52 }, buyPrice: 416,
  },

  // ── DAGA (DAGGER) — OSRS Dagger: 1/15/30/50/70/85 ──
  weapon_dagger_iron: {
    id: 'weapon_dagger_iron', name: 'Daga de Hierro', sellable: true, icon: 'dagger2',
    type: ItemType.EQUIPMENT, subtype: WeaponSubtype.DAGGER, slot: 'weapon', weaponId: 30,
    weaponHand: 'one', attackWidth: 1, attackLength: 1, attackStaminaCost: 3,
    materialTier: 'iron', requiredSmithingLevel: 15,
    modifiers: { minDamage: 4, maxDamage: 9 }, buyPrice: 81,
  },
  weapon_dagger_steel: {
    id: 'weapon_dagger_steel', name: 'Daga de Acero', sellable: true, icon: 'dagger3',
    type: ItemType.EQUIPMENT, subtype: WeaponSubtype.DAGGER, slot: 'weapon', weaponId: 31,
    weaponHand: 'one', attackWidth: 1, attackLength: 1, attackStaminaCost: 3,
    materialTier: 'steel', requiredSmithingLevel: 30,
    modifiers: { minDamage: 5, maxDamage: 13 }, buyPrice: 108,
  },
  weapon_dagger_mithril: {
    id: 'weapon_dagger_mithril', name: 'Daga de Mithril', sellable: true, icon: 'dagger4',
    type: ItemType.EQUIPMENT, subtype: WeaponSubtype.DAGGER, slot: 'weapon', weaponId: 32,
    weaponHand: 'one', attackWidth: 1, attackLength: 1, attackStaminaCost: 3,
    materialTier: 'mithril', requiredSmithingLevel: 50,
    modifiers: { minDamage: 8, maxDamage: 18 }, buyPrice: 150,
  },
  weapon_dagger_adamant: {
    id: 'weapon_dagger_adamant', name: 'Daga de Adamantita', sellable: true, icon: 'dagger5',
    type: ItemType.EQUIPMENT, subtype: WeaponSubtype.DAGGER, slot: 'weapon', weaponId: 33,
    weaponHand: 'one', attackWidth: 1, attackLength: 1, attackStaminaCost: 3,
    materialTier: 'adamant', requiredSmithingLevel: 70,
    modifiers: { minDamage: 10, maxDamage: 23 }, buyPrice: 198,
  },
  weapon_dagger_rune: {
    id: 'weapon_dagger_rune', name: 'Daga Rúnica', sellable: true, icon: 'dagger6',
    type: ItemType.EQUIPMENT, subtype: WeaponSubtype.DAGGER, slot: 'weapon', weaponId: 34,
    weaponHand: 'one', attackWidth: 1, attackLength: 1, attackStaminaCost: 3,
    materialTier: 'rune', requiredSmithingLevel: 85,
    modifiers: { minDamage: 13, maxDamage: 29 }, buyPrice: 252,
  },
  weapon_dagger_obsidian: {
    id: 'weapon_dagger_obsidian', name: 'Daga de Obsidiana', sellable: true, icon: 'dagger7',
    type: ItemType.EQUIPMENT, subtype: WeaponSubtype.DAGGER, slot: 'weapon', weaponId: 35,
    weaponHand: 'one', attackWidth: 1, attackLength: 1, attackStaminaCost: 3,
    materialTier: 'obsidian', requiredSmithingLevel: 92,
    modifiers: { minDamage: 14, maxDamage: 33 }, buyPrice: 282,
  },
  weapon_dagger_dragon: {
    id: 'weapon_dagger_dragon', name: 'Daga de Dragón', sellable: true, icon: 'dagger8',
    type: ItemType.EQUIPMENT, subtype: WeaponSubtype.DAGGER, slot: 'weapon', weaponId: 36,
    weaponHand: 'one', attackWidth: 1, attackLength: 1, attackStaminaCost: 3,
    materialTier: 'dragon',
    modifiers: { minDamage: 16, maxDamage: 36 }, buyPrice: 312,
  },

  // ── HACHA (AXE) — OSRS Battleaxe: 10/25/40/60/80/95 ──
  weapon_axe_iron: {
    id: 'weapon_axe_iron', name: 'Hacha de Hierro', sellable: true, icon: 'axe5',
    type: ItemType.EQUIPMENT, subtype: WeaponSubtype.AXE, slot: 'weapon', weaponId: 37,
    weaponHand: 'one', attackWidth: 3, attackLength: 1, attackStaminaCost: 7,
    materialTier: 'iron', requiredSmithingLevel: 25,
    modifiers: { minDamage: 9, maxDamage: 16 }, buyPrice: 135,
  },
  weapon_axe_steel: {
    id: 'weapon_axe_steel', name: 'Hacha de Acero', sellable: true, icon: 'axe6',
    type: ItemType.EQUIPMENT, subtype: WeaponSubtype.AXE, slot: 'weapon', weaponId: 38,
    weaponHand: 'one', attackWidth: 3, attackLength: 1, attackStaminaCost: 7,
    materialTier: 'steel', requiredSmithingLevel: 40,
    modifiers: { minDamage: 13, maxDamage: 22 }, buyPrice: 180,
  },
  weapon_axe_mithril: {
    id: 'weapon_axe_mithril', name: 'Hacha de Mithril', sellable: true, icon: 'axe7',
    type: ItemType.EQUIPMENT, subtype: WeaponSubtype.AXE, slot: 'weapon', weaponId: 39,
    weaponHand: 'one', attackWidth: 3, attackLength: 1, attackStaminaCost: 7,
    materialTier: 'mithril', requiredSmithingLevel: 60,
    modifiers: { minDamage: 18, maxDamage: 30 }, buyPrice: 250,
  },
  weapon_axe_adamant: {
    id: 'weapon_axe_adamant', name: 'Hacha de Adamantita', sellable: true, icon: 'axe8',
    type: ItemType.EQUIPMENT, subtype: WeaponSubtype.AXE, slot: 'weapon', weaponId: 40,
    weaponHand: 'one', attackWidth: 3, attackLength: 1, attackStaminaCost: 7,
    materialTier: 'adamant', requiredSmithingLevel: 80,
    modifiers: { minDamage: 23, maxDamage: 40 }, buyPrice: 330,
  },
  weapon_axe_rune: {
    id: 'weapon_axe_rune', name: 'Hacha Rúnica', sellable: true, icon: 'axe9',
    type: ItemType.EQUIPMENT, subtype: WeaponSubtype.AXE, slot: 'weapon', weaponId: 41,
    weaponHand: 'one', attackWidth: 3, attackLength: 1, attackStaminaCost: 7,
    materialTier: 'rune', requiredSmithingLevel: 95,
    modifiers: { minDamage: 29, maxDamage: 50 }, buyPrice: 420,
  },
  weapon_axe_obsidian: {
    id: 'weapon_axe_obsidian', name: 'Hacha de Obsidiana', sellable: true, icon: 'axe10',
    type: ItemType.EQUIPMENT, subtype: WeaponSubtype.AXE, slot: 'weapon', weaponId: 42,
    weaponHand: 'one', attackWidth: 3, attackLength: 1, attackStaminaCost: 7,
    materialTier: 'obsidian', requiredSmithingLevel: 97,
    modifiers: { minDamage: 33, maxDamage: 56 }, buyPrice: 470,
  },
  weapon_axe_dragon: {
    id: 'weapon_axe_dragon', name: 'Hacha de Dragón', sellable: true, icon: 'axe11',
    type: ItemType.EQUIPMENT, subtype: WeaponSubtype.AXE, slot: 'weapon', weaponId: 43,
    weaponHand: 'one', attackWidth: 3, attackLength: 1, attackStaminaCost: 7,
    materialTier: 'dragon',
    modifiers: { minDamage: 36, maxDamage: 62 }, buyPrice: 520,
  },

  // ── CASCO (HELM) — OSRS Full helm: 7/22/37/57/77/92 ──
  armor_helm_bronze: {
    id: 'armor_helm_bronze', name: 'Casco de Bronce', sellable: true, icon: 'noicon',
    type: ItemType.EQUIPMENT, subtype: ArmorSubtype.HEAVY, slot: 'head', helmId: 1,
    materialTier: 'bronze', requiredSmithingLevel: 7, modifiers: { physDef: 2 }, buyPrice: 50,
  },
  armor_helm_iron: {
    id: 'armor_helm_iron', name: 'Casco de Hierro', sellable: true, icon: 'helm40',
    type: ItemType.EQUIPMENT, subtype: ArmorSubtype.HEAVY, slot: 'head', helmId: 40,
    materialTier: 'iron', requiredSmithingLevel: 22, modifiers: { physDef: 3 }, buyPrice: 68,
  },
  armor_helm_steel: {
    id: 'armor_helm_steel', name: 'Casco de Acero', sellable: true, icon: 'helm41',
    type: ItemType.EQUIPMENT, subtype: ArmorSubtype.HEAVY, slot: 'head', helmId: 41,
    materialTier: 'steel', requiredSmithingLevel: 37, modifiers: { physDef: 4 }, buyPrice: 90,
  },
  armor_helm_mithril: {
    id: 'armor_helm_mithril', name: 'Casco de Mithril', sellable: true, icon: 'helm42',
    type: ItemType.EQUIPMENT, subtype: ArmorSubtype.HEAVY, slot: 'head', helmId: 42,
    materialTier: 'mithril', requiredSmithingLevel: 57, modifiers: { physDef: 5 }, buyPrice: 125,
  },
  armor_helm_adamant: {
    id: 'armor_helm_adamant', name: 'Casco de Adamantita', sellable: true, icon: 'helm43',
    type: ItemType.EQUIPMENT, subtype: ArmorSubtype.HEAVY, slot: 'head', helmId: 43,
    materialTier: 'adamant', requiredSmithingLevel: 77, modifiers: { physDef: 7 }, buyPrice: 165,
  },
  armor_helm_rune: {
    id: 'armor_helm_rune', name: 'Casco Rúnico', sellable: true, icon: 'helm44',
    type: ItemType.EQUIPMENT, subtype: ArmorSubtype.HEAVY, slot: 'head', helmId: 44,
    materialTier: 'rune', requiredSmithingLevel: 92, modifiers: { physDef: 8 }, buyPrice: 210,
  },
  armor_helm_obsidian: {
    id: 'armor_helm_obsidian', name: 'Casco de Obsidiana', sellable: true, icon: 'helm45',
    type: ItemType.EQUIPMENT, subtype: ArmorSubtype.HEAVY, slot: 'head', helmId: 45,
    materialTier: 'obsidian', requiredSmithingLevel: 96, modifiers: { physDef: 9 }, buyPrice: 235,
  },
  armor_helm_dragon: {
    id: 'armor_helm_dragon', name: 'Casco de Dragón', sellable: true, icon: 'helm46',
    type: ItemType.EQUIPMENT, subtype: ArmorSubtype.HEAVY, slot: 'head', helmId: 46,
    materialTier: 'dragon', modifiers: { physDef: 10 }, buyPrice: 260,
  },

  // ── ESCUDO (SHIELD) — OSRS Square shield: 8/23/38/58/78/93 ──
  armor_shield_bronze: {
    id: 'armor_shield_bronze', name: 'Escudo de Bronce', sellable: true, icon: 'noicon',
    type: ItemType.EQUIPMENT, subtype: ArmorSubtype.HEAVY, slot: 'shield', shieldId: 1,
    materialTier: 'bronze', requiredSmithingLevel: 8, modifiers: { physDef: 2 }, buyPrice: 55,
  },
  armor_shield_iron: {
    id: 'armor_shield_iron', name: 'Escudo de Hierro', sellable: true, icon: 'shield30',
    type: ItemType.EQUIPMENT, subtype: ArmorSubtype.HEAVY, slot: 'shield', shieldId: 30,
    materialTier: 'iron', requiredSmithingLevel: 23, modifiers: { physDef: 3 }, buyPrice: 74,
  },
  armor_shield_steel: {
    id: 'armor_shield_steel', name: 'Escudo de Acero', sellable: true, icon: 'shield31',
    type: ItemType.EQUIPMENT, subtype: ArmorSubtype.HEAVY, slot: 'shield', shieldId: 31,
    materialTier: 'steel', requiredSmithingLevel: 38, modifiers: { physDef: 4 }, buyPrice: 99,
  },
  armor_shield_mithril: {
    id: 'armor_shield_mithril', name: 'Escudo de Mithril', sellable: true, icon: 'shield32',
    type: ItemType.EQUIPMENT, subtype: ArmorSubtype.HEAVY, slot: 'shield', shieldId: 32,
    materialTier: 'mithril', requiredSmithingLevel: 58, modifiers: { physDef: 5 }, buyPrice: 138,
  },
  armor_shield_adamant: {
    id: 'armor_shield_adamant', name: 'Escudo de Adamantita', sellable: true, icon: 'shield33',
    type: ItemType.EQUIPMENT, subtype: ArmorSubtype.HEAVY, slot: 'shield', shieldId: 33,
    materialTier: 'adamant', requiredSmithingLevel: 78, modifiers: { physDef: 7 }, buyPrice: 182,
  },
  armor_shield_rune: {
    id: 'armor_shield_rune', name: 'Escudo Rúnico', sellable: true, icon: 'shield34',
    type: ItemType.EQUIPMENT, subtype: ArmorSubtype.HEAVY, slot: 'shield', shieldId: 34,
    materialTier: 'rune', requiredSmithingLevel: 93, modifiers: { physDef: 8 }, buyPrice: 231,
  },
  armor_shield_obsidian: {
    id: 'armor_shield_obsidian', name: 'Escudo de Obsidiana', sellable: true, icon: 'shield35',
    type: ItemType.EQUIPMENT, subtype: ArmorSubtype.HEAVY, slot: 'shield', shieldId: 35,
    materialTier: 'obsidian', requiredSmithingLevel: 96, modifiers: { physDef: 9 }, buyPrice: 259,
  },
  armor_shield_dragon: {
    id: 'armor_shield_dragon', name: 'Escudo de Dragón', sellable: true, icon: 'shield36',
    type: ItemType.EQUIPMENT, subtype: ArmorSubtype.HEAVY, slot: 'shield', shieldId: 36,
    materialTier: 'dragon', modifiers: { physDef: 10 }, buyPrice: 286,
  },

  // ── ARMADURA (BODY) — OSRS Chainbody: 11/26/41/61/81/96 (bronce = armor_body_001) ──
  armor_body_iron: {
    id: 'armor_body_iron', name: 'Armadura de Hierro', sellable: true, icon: 'body30',
    type: ItemType.EQUIPMENT, subtype: ArmorSubtype.HEAVY, slot: 'body', bodyId: 30,
    materialTier: 'iron', requiredSmithingLevel: 26, modifiers: { physDef: 4, moveSpeedPercent: -0.08 }, buyPrice: 81,
  },
  armor_body_steel: {
    id: 'armor_body_steel', name: 'Armadura de Acero', sellable: true, icon: 'body31',
    type: ItemType.EQUIPMENT, subtype: ArmorSubtype.HEAVY, slot: 'body', bodyId: 31,
    materialTier: 'steel', requiredSmithingLevel: 41, modifiers: { physDef: 5, moveSpeedPercent: -0.08 }, buyPrice: 108,
  },
  armor_body_mithril: {
    id: 'armor_body_mithril', name: 'Armadura de Mithril', sellable: true, icon: 'body32',
    type: ItemType.EQUIPMENT, subtype: ArmorSubtype.HEAVY, slot: 'body', bodyId: 32,
    materialTier: 'mithril', requiredSmithingLevel: 61, modifiers: { physDef: 8, moveSpeedPercent: -0.08 }, buyPrice: 150,
  },
  armor_body_adamant: {
    id: 'armor_body_adamant', name: 'Armadura de Adamantita', sellable: true, icon: 'body33',
    type: ItemType.EQUIPMENT, subtype: ArmorSubtype.HEAVY, slot: 'body', bodyId: 33,
    materialTier: 'adamant', requiredSmithingLevel: 81, modifiers: { physDef: 10, moveSpeedPercent: -0.08 }, buyPrice: 198,
  },
  armor_body_rune: {
    id: 'armor_body_rune', name: 'Armadura Rúnica', sellable: true, icon: 'body34',
    type: ItemType.EQUIPMENT, subtype: ArmorSubtype.HEAVY, slot: 'body', bodyId: 34,
    materialTier: 'rune', requiredSmithingLevel: 96, modifiers: { physDef: 13, moveSpeedPercent: -0.08 }, buyPrice: 252,
  },
  armor_body_obsidian: {
    id: 'armor_body_obsidian', name: 'Armadura de Obsidiana', sellable: true, icon: 'body35',
    type: ItemType.EQUIPMENT, subtype: ArmorSubtype.HEAVY, slot: 'body', bodyId: 35,
    materialTier: 'obsidian', requiredSmithingLevel: 98, modifiers: { physDef: 14, moveSpeedPercent: -0.08 }, buyPrice: 282,
  },
  armor_body_dragon: {
    id: 'armor_body_dragon', name: 'Armadura de Dragón', sellable: true, icon: 'body36',
    type: ItemType.EQUIPMENT, subtype: ArmorSubtype.HEAVY, slot: 'body', bodyId: 36,
    materialTier: 'dragon', modifiers: { physDef: 16, moveSpeedPercent: -0.08 }, buyPrice: 312,
  },

  // ── CLASES DE ARMADURA LIVIANAS (Cuero / Tela). Ver shared/data/armorClasses.ts.
  //    Cuero (LIGHT): media, sin penalización, ~0.6× physDef del metal. Casco (hay arte).
  //    Tela (CLOTH): ligera para clases mágicas, ~0.35× physDef. Casco + cuerpo (hay arte).
  //    physDef derivada de la curva metal por slot; Metal/Cuero se forjan en Herrería, Tela
  //    se confecciona en Sastrería (crafteo = fase futura). ──
  // CASCO CUERO (LIGHT) — helm50..56
  armor_helm_cuero_iron: {
    id: 'armor_helm_cuero_iron', name: 'Casco de Cuero (Hierro)', sellable: true, icon: 'helm50',
    type: ItemType.EQUIPMENT, subtype: ArmorSubtype.LIGHT, slot: 'head', helmId: 50,
    materialTier: 'iron', requiredSmithingLevel: 22, modifiers: { physDef: 2 }, buyPrice: 48,
  },
  armor_helm_cuero_steel: {
    id: 'armor_helm_cuero_steel', name: 'Casco de Cuero (Acero)', sellable: true, icon: 'helm51',
    type: ItemType.EQUIPMENT, subtype: ArmorSubtype.LIGHT, slot: 'head', helmId: 51,
    materialTier: 'steel', requiredSmithingLevel: 37, modifiers: { physDef: 2 }, buyPrice: 63,
  },
  armor_helm_cuero_mithril: {
    id: 'armor_helm_cuero_mithril', name: 'Casco de Cuero (Mithril)', sellable: true, icon: 'helm52',
    type: ItemType.EQUIPMENT, subtype: ArmorSubtype.LIGHT, slot: 'head', helmId: 52,
    materialTier: 'mithril', requiredSmithingLevel: 57, modifiers: { physDef: 3 }, buyPrice: 88,
  },
  armor_helm_cuero_adamant: {
    id: 'armor_helm_cuero_adamant', name: 'Casco de Cuero (Adamantita)', sellable: true, icon: 'helm53',
    type: ItemType.EQUIPMENT, subtype: ArmorSubtype.LIGHT, slot: 'head', helmId: 53,
    materialTier: 'adamant', requiredSmithingLevel: 77, modifiers: { physDef: 4 }, buyPrice: 116,
  },
  armor_helm_cuero_rune: {
    id: 'armor_helm_cuero_rune', name: 'Casco de Cuero (Runita)', sellable: true, icon: 'helm54',
    type: ItemType.EQUIPMENT, subtype: ArmorSubtype.LIGHT, slot: 'head', helmId: 54,
    materialTier: 'rune', requiredSmithingLevel: 92, modifiers: { physDef: 5 }, buyPrice: 147,
  },
  armor_helm_cuero_obsidian: {
    id: 'armor_helm_cuero_obsidian', name: 'Casco de Cuero (Obsidiana)', sellable: true, icon: 'helm55',
    type: ItemType.EQUIPMENT, subtype: ArmorSubtype.LIGHT, slot: 'head', helmId: 55,
    materialTier: 'obsidian', requiredSmithingLevel: 96, modifiers: { physDef: 5 }, buyPrice: 165,
  },
  armor_helm_cuero_dragon: {
    id: 'armor_helm_cuero_dragon', name: 'Casco de Cuero (Dragón)', sellable: true, icon: 'helm56',
    type: ItemType.EQUIPMENT, subtype: ArmorSubtype.LIGHT, slot: 'head', helmId: 56,
    materialTier: 'dragon', modifiers: { physDef: 6 }, buyPrice: 182,
  },
  // NOTA (Tela): la línea TELA (mágica, Sastrería) YA existe como `armor_cloth_*`
  // (lino/lana/seda/místico, magicDef) generada más abajo — NO se duplica acá. Se integró a este
  // sistema de clases dándole `subtype: ArmorSubtype.CLOTH` (queda gateada como Tela). El arte de
  // tela generado (`helm60-66`, `body37-43`) queda DISPONIBLE para que esa línea lo adopte cuando
  // tenga casco/íconos propios. Ver docs/VISUAL_ASSETS.md y shared/data/armorClasses.ts.

  // ── BOTAS (BOOTS) — OSRS no forja botas de metal → nivel extrapolado (med helm) ──
  armor_boots_bronze: {
    id: 'armor_boots_bronze', name: 'Botas de Bronce', sellable: true, icon: 'noicon',
    type: ItemType.EQUIPMENT, subtype: ArmorSubtype.HEAVY, slot: 'boots', bootId: 1,
    materialTier: 'bronze', requiredSmithingLevel: 3, modifiers: { physDef: 1 }, buyPrice: 40,
  },
  armor_boots_iron: {
    id: 'armor_boots_iron', name: 'Botas de Hierro', sellable: true, icon: 'noicon',
    type: ItemType.EQUIPMENT, subtype: ArmorSubtype.HEAVY, slot: 'boots', bootId: 1,
    materialTier: 'iron', requiredSmithingLevel: 18, modifiers: { physDef: 1 }, buyPrice: 54,
  },
  armor_boots_steel: {
    id: 'armor_boots_steel', name: 'Botas de Acero', sellable: true, icon: 'noicon',
    type: ItemType.EQUIPMENT, subtype: ArmorSubtype.HEAVY, slot: 'boots', bootId: 1,
    materialTier: 'steel', requiredSmithingLevel: 33, modifiers: { physDef: 2 }, buyPrice: 72,
  },
  armor_boots_mithril: {
    id: 'armor_boots_mithril', name: 'Botas de Mithril', sellable: true, icon: 'noicon',
    type: ItemType.EQUIPMENT, subtype: ArmorSubtype.HEAVY, slot: 'boots', bootId: 1,
    materialTier: 'mithril', requiredSmithingLevel: 53, modifiers: { physDef: 3 }, buyPrice: 100,
  },
  armor_boots_adamant: {
    id: 'armor_boots_adamant', name: 'Botas de Adamantita', sellable: true, icon: 'noicon',
    type: ItemType.EQUIPMENT, subtype: ArmorSubtype.HEAVY, slot: 'boots', bootId: 1,
    materialTier: 'adamant', requiredSmithingLevel: 73, modifiers: { physDef: 3 }, buyPrice: 132,
  },
  armor_boots_rune: {
    id: 'armor_boots_rune', name: 'Botas Rúnicas', sellable: true, icon: 'noicon',
    type: ItemType.EQUIPMENT, subtype: ArmorSubtype.HEAVY, slot: 'boots', bootId: 1,
    materialTier: 'rune', requiredSmithingLevel: 88, modifiers: { physDef: 4 }, buyPrice: 168,
  },
  armor_boots_obsidian: {
    id: 'armor_boots_obsidian', name: 'Botas de Obsidiana', sellable: true, icon: 'noicon',
    type: ItemType.EQUIPMENT, subtype: ArmorSubtype.HEAVY, slot: 'boots', bootId: 1,
    materialTier: 'obsidian', requiredSmithingLevel: 93, modifiers: { physDef: 5 }, buyPrice: 188,
  },
  armor_boots_dragon: {
    id: 'armor_boots_dragon', name: 'Botas de Dragón', sellable: true, icon: 'noicon',
    type: ItemType.EQUIPMENT, subtype: ArmorSubtype.HEAVY, slot: 'boots', bootId: 1,
    materialTier: 'dragon', modifiers: { physDef: 5 }, buyPrice: 208,
  },

  // ===== BARCOS (slot 'boat') =====
  // Permiten navegar sobre agua profunda. Equip sólo con agua navegable enfrente;
  // unequip sólo parado en tierra o con tierra caminable enfrente. Navegando, el
  // visual del personaje pasa a ser el barco (bodyId 20-22) y se ocultan cabeza/
  // arma/escudo/etc. Combate completo permitido a bordo — ahí pegan los bonus de
  // daño por categoría (melee/proyectil/hechizo). Ver docs/WATER_AND_BOATS.md.
  //
  // ANOTADO (futuro): cuando exista la skill de PESCA, sumar un barco pesquero
  // (ej. boat_fishing_001) barato con `fishingBonus` — el modifier ya existe como
  // placeholder en ItemModifiers.
  //
  // Ícono 32x32 en assets/item/ships/<icon>.png (resuelto por slot 'boat' en
  // GameScene.preloadItemIcons); spritesheet de navegación en
  // assets/entities/body/anims/body<bodyId>.png (placeholder hasta arte real).
  boat_war_001: {
    id: 'boat_war_001',
    name: 'Barco de Guerra',
    sellable: true,
    icon: 'boat_war',
    type: ItemType.EQUIPMENT,
    slot: 'boat',
    bodyId: 20,
    affixable: true,
    description:
      'Casco reforzado con placas de hierro. Hecho para abordar: potencia el filo de sus tripulantes.',
    modifiers: {
      meleeDamagePercent: 0.15,
      physDef: 8,
      moveSpeed: 0.2,
    },
    buyPrice: 5000,
  },
  boat_hunter_001: {
    id: 'boat_hunter_001',
    name: 'Barco Cazador',
    sellable: true,
    icon: 'boat_hunter',
    type: ItemType.EQUIPMENT,
    slot: 'boat',
    bodyId: 21,
    affixable: true,
    description:
      'Ligero y esquivo, con plataformas de tiro elevadas. El favorito de arqueros y ballesteros.',
    modifiers: {
      projectileDamagePercent: 0.15,
      evasion: 0.05,
      moveSpeed: 0.4,
    },
    buyPrice: 5000,
  },
  boat_arcane_001: {
    id: 'boat_arcane_001',
    name: 'Barco Arcano',
    sellable: true,
    icon: 'boat_arcane',
    type: ItemType.EQUIPMENT,
    slot: 'boat',
    bodyId: 22,
    affixable: true,
    description:
      'Su mástil es un conducto de maná tallado en cristal. Las mareas obedecen a quien conjura desde su cubierta.',
    modifiers: {
      spellDamagePercent: 0.15,
      magicDef: 8,
      moveSpeed: 0.3,
    },
    buyPrice: 5000,
  },
  // Bote humilde: opción de entrada, barato, sin bonus de categoría. Pensado como
  // el barco pesquero (cuando exista la skill de PESCA se le sumará `fishingBonus`).
  boat_fishing_001: {
    id: 'boat_fishing_001',
    name: 'Bote Pesquero',
    sellable: true,
    icon: 'boat_fishing',
    type: ItemType.EQUIPMENT,
    slot: 'boat',
    bodyId: 23,
    affixable: true,
    description:
      'Una canoa modesta de pescador. Lenta y sin filo, pero flota y llega a mar abierto sin vaciar la bolsa.',
    modifiers: {
      moveSpeed: 0.1,
    },
    buyPrice: 800,
  },

  // ===== ACCESORIOS ÚNICOS (modificadores únicos, no rolean) =====
  // unique: true → LEGENDARY con mods FIJOS del template. Los modifiers
  // seeDeadNames/talkToDead son "mods únicos" (ver shared/data/uniqueModifiers.ts):
  // NO están en el pool de afijos, así que nunca salen en drops random ni tienen
  // tiers. Iconos pendientes de arte real (32x32) → usan 'noicon' por ahora.
  amulet_spectral_sight: {
    id: 'amulet_spectral_sight',
    name: 'Amuleto de Visión Espectral',
    sellable: true,
    icon: 'amulets/amulet5',
    type: ItemType.EQUIPMENT,
    slot: 'amulet',
    unique: true,
    description:
      'Un ojo de cristal engarzado que percibe a las almas errantes. Quien lo porta ve el nombre de los muertos.',
    modifiers: {
      seeDeadNames: 1,
    },
    buyPrice: 1500,
  },
  // Ítem demo de SKILLING: prueba visible de los mods skillXpBonus/skillEffectiveness.
  // +10% XP de Minería y +5% efectividad (chance de mena extra). Ver docs/SKILLS_SYSTEM.md.
  amulet_miner_001: {
    id: 'amulet_miner_001',
    name: 'Amuleto del Minero',
    sellable: true,
    icon: 'amulets/amulet30',
    type: ItemType.EQUIPMENT,
    slot: 'amulet',
    unique: true,
    description:
      'Un fragmento de veta pura colgado de una cadena de hierro. Guía el pico hacia el corazón de la roca.',
    modifiers: {
      skillXpBonus: { skill_mining: 0.1 },
      skillEffectiveness: { skill_mining: 0.05 },
    },
    buyPrice: 2000,
  },
  // Poder de PARTY: habilita el control de reparto de XP del grupo (mod único
  // partyXpControl). Ver docs/PARTY_SYSTEM.md §Reparto de XP configurable.
  amulet_party_leader: {
    id: 'amulet_party_leader',
    name: 'Amuleto del Pacto',
    sellable: true,
    icon: 'amulets/amulet19',
    type: ItemType.EQUIPMENT,
    slot: 'amulet',
    unique: true,
    description:
      'Un sello de líder forjado en oro viejo. Quien lo porta reparte a su antojo la experiencia del grupo.',
    modifiers: {
      partyXpControl: 1,
    },
    buyPrice: 3000,
  },
  ring_deathwhisper: {
    id: 'ring_deathwhisper',
    name: 'Anillo del Susurro',
    sellable: true,
    icon: 'rings/ring64',
    type: ItemType.EQUIPMENT,
    slot: 'ring',
    unique: true,
    description:
      'Forjado con hueso de un oráculo. Permite oír y responder a las voces del más allá.',
    modifiers: {
      talkToDead: 1,
    },
    buyPrice: 1500,
  },
  ring_executioner: {
    id: 'ring_executioner',
    name: 'Anillo del Verdugo',
    sellable: true,
    icon: 'rings/ring43',
    type: ItemType.EQUIPMENT,
    slot: 'ring',
    unique: true,
    description:
      'Forjado con el filo de mil sentencias. Cada golpe de quien lo porta cae como un veredicto: siempre crítico.',
    modifiers: {
      alwaysCrit: 1,
    },
    buyPrice: 2500,
  },
  ring_backstab_axe: {
    id: 'ring_backstab_axe',
    name: 'Colmillo Traicionero',
    sellable: true,
    icon: 'rings/ring91',
    type: ItemType.EQUIPMENT,
    slot: 'ring',
    unique: true,
    // PLACEHOLDER: recordatorio de que esta lógica (backstab crítico por tipo de arma +
    // reducción de daño crítico como mod aparte) irá luego en un HACHA única. Este amuleto
    // la ejercita end-to-end: crítico garantizado al golpear la espalda con HACHAS, a costa
    // de 100% menos daño de crítico (el crítico pega como un golpe normal). Ver COMBAT_SYSTEMS.md.
    description:
      'Un colmillo de bestia que enseña a leer la espalda del enemigo. Con un hacha, todo golpe por la espalda es certero, aunque tus críticos ya no desgarran de más.',
    modifiers: {
      backstabCrit: { axe: 1 },
      critDamageReductionPercent: 1.0,
    },
    buyPrice: 2500,
  },
  ring_omniblock: {
    id: 'ring_omniblock',
    name: 'Égida del Centinela',
    sellable: true,
    icon: 'rings/ring68',
    type: ItemType.EQUIPMENT,
    slot: 'ring',
    unique: true,
    description:
      'Un ojo grabado que vigila en todas direcciones. Quien lo porta puede alzar su guardia contra golpes que vengan de cualquier lado, no sólo de frente.',
    modifiers: {
      blockOmnidirectional: 1,
      blockChance: 0.1,
    },
    buyPrice: 2500,
  },
  ring_knockback: {
    id: 'ring_knockback',
    name: 'Puño del Titán',
    sellable: true,
    icon: 'rings/ring67',
    type: ItemType.EQUIPMENT,
    slot: 'ring',
    unique: true,
    description:
      'Late con la fuerza de un gigante dormido. Cada golpe de quien lo porta manda a su víctima despedida hacia atrás.',
    modifiers: {
      knockbackOnHitTiles: 1,
    },
    buyPrice: 2500,
  },
  ring_glass_cannon: {
    id: 'ring_glass_cannon',
    name: 'Corazón de Cristal',
    sellable: true,
    icon: 'rings/ring53',
    unique: true,
    type: ItemType.EQUIPMENT,
    slot: 'ring',
    // Bonus con contrapartida: mucho daño a costa de mucha defensa (build glass-cannon).
    description:
      'Un cristal que canaliza una potencia brutal, pero deja a su portador tan frágil como el vidrio. Pegás mucho más fuerte, pero cualquier golpe te duele el doble.',
    modifiers: {
      damagePercent: 0.3,
      physDef: -12,
      magicDef: -12,
    },
    buyPrice: 2500,
  },
  amulet_silence_vow: {
    id: 'amulet_silence_vow',
    name: 'Mordaza del Asceta',
    sellable: true,
    icon: 'amulets/amulet24',
    type: ItemType.EQUIPMENT,
    slot: 'amulet',
    unique: true,
    description:
      'Un sello de plata que ata la voz del portador. Quien lo lleva renuncia a toda magia: no puede pronunciar conjuro alguno.',
    modifiers: {
      selfSilence: 1,
    },
    buyPrice: 1500,
  },
  amulet_aura_singularity: {
    id: 'amulet_aura_singularity',
    name: 'Cáliz del Aura Única',
    sellable: true,
    icon: 'amulets/amulet17',
    type: ItemType.EQUIPMENT,
    slot: 'amulet',
    unique: true,
    description:
      'Concentra todo el poder en una sola llama. Solo podés mantener un aura encendida, pero arde con el doble de fuerza.',
    modifiers: {
      auraSingularity: 1,
    },
    buyPrice: 2500,
  },
  amulet_eternal_roots: {
    id: 'amulet_eternal_roots',
    name: 'Amuleto de Raíces Eternas',
    sellable: true,
    icon: 'amulets/amulet14',
    type: ItemType.EQUIPMENT,
    slot: 'amulet',
    unique: true,
    description:
      'Una semilla viva late en su interior. Cada golpe del portador hace brotar raíces que atrapan al enemigo: queda inmovilizado.',
    modifiers: {
      immobilizeEveryHit: 1,
    },
    buyPrice: 3000,
  },
  ring_unbreakable_will: {
    id: 'ring_unbreakable_will',
    name: 'Anillo de Voluntad Inquebrantable',
    sellable: true,
    icon: 'rings/ring45',
    type: ItemType.EQUIPMENT,
    slot: 'ring',
    unique: true,
    description:
      'Ninguna raíz, hielo ni hechizo puede anclar a quien lo porta. Jamás perdés tu movilidad: no podés ser inmovilizado ni paralizado.',
    modifiers: {
      impairImmunity: 1,
    },
    buyPrice: 3000,
  },
  ring_unbound_range: {
    id: 'ring_unbound_range',
    name: 'Anillo del Conjuro sin Fronteras',
    sellable: true,
    icon: 'rings/ring88',
    type: ItemType.EQUIPMENT,
    slot: 'ring',
    unique: true,
    description:
      'El horizonte deja de ser un límite. Tus hechizos ya no tienen distancia mínima ni máxima: los lanzás desde donde sea, sin importar cuán cerca o lejos esté tu objetivo.',
    modifiers: {
      unboundSpellRange: 1,
    },
    buyPrice: 3000,
  },
  // ── Únicos de INVISIBILIDAD (Fase 3). Ver docs/INVISIBILITY.md. ──
  amulet_eternal_veil: {
    id: 'amulet_eternal_veil',
    name: 'Amuleto del Velo Eterno',
    sellable: true,
    icon: 'amulets/amulet18',
    type: ItemType.EQUIPMENT,
    slot: 'amulet',
    unique: true,
    description:
      'Las sombras que te envuelven ya no se disipan. Tu invisibilidad no expira con el tiempo: permanece hasta que la rompas al atacar.',
    modifiers: {
      invisibilityNeverExpires: 1,
    },
    buyPrice: 3000,
  },
  ring_ghost_step: {
    id: 'ring_ghost_step',
    name: 'Anillo del Paso Espectral',
    sellable: true,
    icon: 'rings/ring20',
    type: ItemType.EQUIPMENT,
    slot: 'ring',
    unique: true,
    description:
      'Cuando desaparecés, te volvés un fantasma completo: mientras estés invisible no dejás huellas ni hacés el menor ruido al caminar.',
    modifiers: {
      silentWhileInvisible: 1,
    },
    buyPrice: 3000,
  },
  ring_revealing_eye: {
    id: 'ring_revealing_eye',
    name: 'Anillo del Ojo Revelador',
    sellable: true,
    icon: 'rings/ring21',
    type: ItemType.EQUIPMENT,
    slot: 'ring',
    unique: true,
    description:
      'Un ojo que ve más allá del velo. Distinguís a los jugadores invisibles: sus siluetas y nombres se te revelan.',
    modifiers: {
      seeInvisible: 1,
    },
    buyPrice: 3000,
  },
  // Amuleto que OTORGA un hechizo (grantsSpellId) mientras esté EQUIPADO: al ponerlo
  // aprendés "Dardo de Alma" y entra en la hotbar; al sacarlo lo olvidás y sale de la
  // hotbar. El hechizo es grantOnly (no se auto-aprende). Ver docs/SPELLS.md.
  ring_spellbound: {
    id: 'ring_spellbound',
    name: 'Anillo del Conjuro Ligado',
    sellable: true,
    icon: 'rings/ring33',
    type: ItemType.EQUIPMENT,
    slot: 'ring',
    unique: true,
    description:
      'Un alma hechicera atrapada en el cristal te presta su conjuro. Mientras lo portes conocés el Dardo de Alma; al quitártelo, el saber se desvanece.',
    modifiers: {},
    grantsSpellId: 'spell_amulet_soul_bolt',
    buyPrice: 3000,
  },
  // Amuleto que suprime las HUELLAS visuales al caminar (mod único noFootsteps).
  ring_ghoststep: {
    id: 'ring_ghoststep',
    name: 'Anillo del Paso Fantasma',
    sellable: true,
    icon: 'rings/ring13',
    type: ItemType.EQUIPMENT,
    slot: 'ring',
    unique: true,
    description:
      'Tejido con bruma de aparición. Mientras lo portes no dejás huella alguna en el suelo: te movés sin rastro.',
    modifiers: {
      noFootsteps: 1,
    },
    buyPrice: 3000,
  },
  // Amuleto que suprime el RUIDO al caminar (mod único noWalkNoise). El efecto de audio
  // está pendiente (no hay sistema de sonido aún) — ver docs/SOUND_AND_MUSIC.md.
  ring_silent_steps: {
    id: 'ring_silent_steps',
    name: 'Anillo del Andar Silencioso',
    sellable: true,
    icon: 'rings/ring75',
    type: ItemType.EQUIPMENT,
    slot: 'ring',
    unique: true,
    description:
      'Forrado en fieltro encantado. Mientras lo portes tus pasos no hacen ningún ruido, ni el más leve crujido.',
    modifiers: {
      noWalkNoise: 1,
    },
    buyPrice: 3000,
  },

  // ===== AMULETOS / POCIONES DE INVOCACIÓN (ver docs/SUMMONS.md) =====
  // Amuleto que invoca un Guardián mientras esté EQUIPADO (grant, permanente).
  amulet_summon_guardian: {
    id: 'amulet_summon_guardian',
    name: 'Amuleto del Guardián',
    sellable: true,
    icon: 'amulets/amulet13',
    type: ItemType.EQUIPMENT,
    slot: 'amulet',
    unique: true,
    description:
      'Mientras lo portes, un Guardián de Piedra despierta a tu lado y combate por vos. Si cae, vuelve a alzarse tras un tiempo.',
    modifiers: {},
    grantsSummonProfileId: 'summon_guardian',
    buyPrice: 4000,
  },
  // Amuleto que invoca un Guardián PROVOCADOR: jala el aggro de los enemigos hacia sí
  // mismo (tanque). Ver docs/TAUNT.md.
  amulet_summon_provocateur: {
    id: 'amulet_summon_provocateur',
    name: 'Amuleto del Guardián Provocador',
    sellable: true,
    icon: 'amulets/amulet25',
    type: ItemType.EQUIPMENT,
    slot: 'amulet',
    unique: true,
    description:
      'Mientras lo portes, un Guardián Provocador se alza a tu lado y atrae hacia sí la furia de los enemigos cercanos, protegiéndote.',
    modifiers: {},
    grantsSummonProfileId: 'summon_provocateur',
    buyPrice: 4500,
  },
  // Amuleto USABLE con cooldown: invoca un Sabueso temporal al usarlo (tecla Usar / click).
  amulet_summon_warhound: {
    id: 'amulet_summon_warhound',
    name: 'Amuleto del Sabueso',
    sellable: true,
    icon: 'amulets/amulet10',
    type: ItemType.EQUIPMENT,
    slot: 'amulet',
    unique: true,
    description:
      'Usalo para desatar un Sabueso de Guerra que lucha a tu lado durante 30 segundos. Cooldown de 30s.',
    modifiers: {},
    useSummonProfileId: 'summon_warhound',
    useCooldownMs: 30000,
    buyPrice: 3500,
  },
  // Amuleto que habilita el MODO AGRESIVO de las invocaciones (mod único).
  amulet_summon_aggressive: {
    id: 'amulet_summon_aggressive',
    name: 'Amuleto del Instinto Asesino',
    sellable: true,
    icon: 'amulets/amulet27',
    type: ItemType.EQUIPMENT,
    slot: 'amulet',
    unique: true,
    description:
      'Tus invocaciones pueden entrar en modo agresivo: atacan a cualquier enemigo que se cruce. Tocá la tecla de modo para ciclar hasta el modo agresivo.',
    modifiers: {
      summonAggressiveMode: 1,
    },
    buyPrice: 3000,
  },
  // Únicos de BUILD de invocador (mods ya wireados en SummonSystem.computeSummonModifiers).
  amulet_summon_frenzy: {
    id: 'amulet_summon_frenzy',
    name: 'Amuleto del Frenesí',
    sellable: true,
    icon: 'amulets/amulet20',
    type: ItemType.EQUIPMENT,
    slot: 'amulet',
    unique: true,
    description:
      'Tus invocaciones atacan mucho más rápido, pero pierden por completo la capacidad de dar críticos.',
    modifiers: {
      summonFrenzy: 1,
    },
    buyPrice: 4000,
  },
  amulet_summon_executioner: {
    id: 'amulet_summon_executioner',
    name: 'Amuleto del Verdugo Paciente',
    sellable: true,
    icon: 'amulets/amulet15',
    type: ItemType.EQUIPMENT,
    slot: 'amulet',
    unique: true,
    description:
      'Tus invocaciones atacan muy lento, pero cada golpe descarga un daño devastador.',
    modifiers: {
      summonExecutioner: 1,
    },
    buyPrice: 4000,
  },
  amulet_summon_packleader: {
    id: 'amulet_summon_packleader',
    name: 'Amuleto del Líder de la Manada',
    sellable: true,
    icon: 'amulets/amulet4',
    type: ItemType.EQUIPMENT,
    slot: 'amulet',
    unique: true,
    description: 'Cada invocación trae una criatura extra.',
    modifiers: {
      summonPackLeader: 1,
    },
    buyPrice: 5000,
  },
  amulet_summon_multibind: {
    id: 'amulet_summon_multibind',
    name: 'Amuleto del Vínculo Múltiple',
    sellable: true,
    icon: 'amulets/amulet12',
    type: ItemType.EQUIPMENT,
    slot: 'amulet',
    unique: true,
    description:
      'Fija tu límite de invocaciones en 5: podés mantener hasta cinco criaturas invocadas a la vez.',
    modifiers: {
      summonLimitFixed: 5,
    },
    buyPrice: 5000,
  },
  // Ring de STATS de invocador (numéricos, no único → apila con un amuleto de build).
  ring_summon_might: {
    id: 'ring_summon_might',
    name: 'Anillo del Amo de Bestias',
    sellable: true,
    icon: 'rings/ring15',
    type: ItemType.EQUIPMENT,
    slot: 'ring',
    description:
      'Fortalece a tus invocaciones: más vida, daño, velocidad de ataque, crítico y resurrección más veloz.',
    modifiers: {
      summonHpBonus: 40,
      summonPhysicalDamageBonus: 6,
      summonAttackSpeedBonus: 0.15,
      summonCritChance: 0.15,
      summonCritMultiplier: 0.25,
      summonResurrectionSpeed: 0.3,
    },
    buyPrice: 3000,
  },

  // ── Soporte (Fase 4): invocaciones que te asisten (no atacan). ──
  amulet_summon_healer: {
    id: 'amulet_summon_healer',
    name: 'Amuleto del Sanador',
    sellable: true,
    icon: 'amulets/amulet16',
    type: ItemType.EQUIPMENT,
    slot: 'amulet',
    unique: true,
    description:
      'Un Espíritu Sanador te acompaña mientras lo portes y te cura las heridas cada pocos segundos.',
    modifiers: {},
    grantsSummonProfileId: 'summon_healer',
    buyPrice: 4500,
  },
  amulet_summon_manacarrier: {
    id: 'amulet_summon_manacarrier',
    name: 'Amuleto del Maná',
    sellable: true,
    icon: 'amulets/amulet29',
    type: ItemType.EQUIPMENT,
    slot: 'amulet',
    unique: true,
    description:
      'Un Espíritu de Maná te acompaña mientras lo portes y te restaura maná periódicamente.',
    modifiers: {},
    grantsSummonProfileId: 'summon_manacarrier',
    buyPrice: 4000,
  },
  potion_summon_liberator: {
    id: 'potion_summon_liberator',
    name: 'Poción del Liberador',
    sellable: true,
    icon: 'noicon',
    type: ItemType.CONSUMABLE,
    description:
      'Invoca un Espíritu Liberador por 45s que te quita inmovilizar/paralizar cada pocos segundos. Se consume al usar.',
    modifiers: {},
    maxStack: 20,
    useSummonProfileId: 'summon_liberator',
    consumeOnUse: true,
    buyPrice: 350,
  },
  potion_summon_bard: {
    id: 'potion_summon_bard',
    name: 'Poción del Bardo',
    sellable: true,
    icon: 'noicon',
    type: ItemType.CONSUMABLE,
    description:
      'Invoca un Duende Bardo por 45s que te mantiene un buff de daño y velocidad de ataque. Se consume al usar.',
    modifiers: {},
    maxStack: 20,
    useSummonProfileId: 'summon_bard',
    consumeOnUse: true,
    buyPrice: 400,
  },
  // Poción de REVELADO: al usarla revela a los invisibles en 9×9 alrededor tuyo (para
  // todos), con 30s de cooldown. Se consume al usar. Ver docs/INVISIBILITY.md.
  potion_reveal_invisibility: {
    id: 'potion_reveal_invisibility',
    name: 'Poción Reveladora',
    sellable: true,
    icon: 'noicon',
    type: ItemType.CONSUMABLE,
    description:
      'Al beberla, un fogonazo revela a cualquier enemigo invisible en un área de 9×9 a tu alrededor durante unos segundos. Cooldown de 30s. Se consume al usar.',
    modifiers: {},
    maxStack: 20,
    revealInvisibilityRadius: 9,
    consumeOnUse: true,
    useCooldownMs: 30000,
    buyPrice: 600,
  },
  // Poción consumible: invoca un Diablillo temporal (se consume al usar).
  potion_summon_imp: {
    id: 'potion_summon_imp',
    name: 'Poción del Diablillo',
    sellable: true,
    icon: 'noicon',
    type: ItemType.CONSUMABLE,
    description: 'Al beberla, un Diablillo aparece y pelea por vos durante 25 segundos. Se consume al usar.',
    modifiers: {},
    maxStack: 20,
    useSummonProfileId: 'summon_imp',
    consumeOnUse: true,
    buyPrice: 250,
  },
  // Poción consumible: invoca un enjambre de 2 arañas temporales.
  potion_summon_spider: {
    id: 'potion_summon_spider',
    name: 'Poción del Enjambre',
    sellable: true,
    icon: 'noicon',
    type: ItemType.CONSUMABLE,
    description: 'Al beberla, 2 arañas emergen y atacan a tus enemigos durante 20 segundos. Se consume al usar.',
    modifiers: {},
    maxStack: 20,
    useSummonProfileId: 'summon_spider',
    consumeOnUse: true,
    buyPrice: 300,
  },

  // ===== COMIDA (Cocina → buff "Bien Alimentado") =====
  // Comer (USE_ITEM) aplica un TemporaryBuff temporal, UNO activo por vez (refresh),
  // 100% opcional. NO cura al instante: lleva tasas (regen / skillXpBonus /
  // toolBreakReduction). Íconos reales de comida pendientes (art) → 'noicon' por ahora.
  // Demos: una por propósito + un "plato completo" que combina los tres.
  // Ver docs/FISHING_SKILL.md / plan de Comida.
  food_grilled_salmon: {
    id: 'food_grilled_salmon',
    name: 'Salmón asado',
    sellable: true,
    icon: 'noicon',
    type: ItemType.CONSUMABLE,
    description:
      'Un filete de salmón a la brasa. Al comerlo, "Bien Alimentado" acelera tu regeneración de vida, maná y stamina fuera de combate (20 min).',
    modifiers: {},
    maxStack: 20,
    consumeOnUse: true,
    buyPrice: 60,
    foodBuff: {
      name: 'Bien Alimentado: Salmón asado',
      modifiers: { regenHP: 2, regenMana: 2, regenStamina: 2 },
      durationMs: 20 * 60 * 1000,
    },
  },
  food_miners_stew: {
    id: 'food_miners_stew',
    name: 'Guiso del Minero',
    sellable: true,
    icon: 'noicon',
    type: ItemType.CONSUMABLE,
    description:
      'Un guiso contundente de pescado y tubérculos. Al comerlo, ganás +15% de XP de Minería (30 min).',
    modifiers: {},
    maxStack: 20,
    consumeOnUse: true,
    buyPrice: 90,
    foodBuff: {
      name: 'Bien Alimentado: Guiso del Minero',
      modifiers: { skillXpBonus: { skill_mining: 0.15 } },
      durationMs: 30 * 60 * 1000,
    },
  },
  food_ironbelly_pie: {
    id: 'food_ironbelly_pie',
    name: 'Empanada Ferropanza',
    sellable: true,
    icon: 'noicon',
    type: ItemType.CONSUMABLE,
    description:
      'Masa densa rellena de pescado ahumado. Al comerla, tus herramientas se rompen un 50% menos seguido (30 min).',
    modifiers: {},
    maxStack: 20,
    consumeOnUse: true,
    buyPrice: 90,
    foodBuff: {
      name: 'Bien Alimentado: Empanada Ferropanza',
      modifiers: { toolBreakReduction: 0.5 },
      durationMs: 30 * 60 * 1000,
    },
  },
  food_feast_platter: {
    id: 'food_feast_platter',
    name: 'Plato del Festín',
    sellable: true,
    icon: 'noicon',
    type: ItemType.CONSUMABLE,
    description:
      'Una receta completa de cocinero: pescado, hierbas y guarnición. "Bien Alimentado" pleno: regeneración, +10% XP de Minería y −25% de rotura de herramientas (30 min).',
    modifiers: {},
    maxStack: 20,
    consumeOnUse: true,
    buyPrice: 200,
    foodBuff: {
      name: 'Bien Alimentado: Plato del Festín',
      modifiers: {
        regenHP: 2,
        regenMana: 2,
        regenStamina: 2,
        skillXpBonus: { skill_mining: 0.1 },
        toolBreakReduction: 0.25,
      },
      durationMs: 30 * 60 * 1000,
    },
  },

  // ===== MATERIALES FUNGIBLES (Exchange) =====
  // Cualquier material stackeable (no affixable/unique/setId) es fungible y se
  // comercia en el EXCHANGE (menas `material_ore_<tier>`, lingotes
  // `material_ingot_<tier>`, maderas, etc., generados más abajo). Los viejos
  // materiales de prueba (`material_mana_essence`, `material_iron_ingot`) se
  // sacaron: la base de crafteo usa los reales. Ver docs/ECONOMY_EXCHANGE_MARKETPLACE.md.

  // ===== MONEDA PREMIUM — Bond (Sello de Aureum) =====
  // Ítem-moneda tradeable (estilo OSRS Bond / EVE PLEX). Se compra al NPC Cambista
  // por mucho oro (gold sink fuerte) y se comercia entre players en el Exchange
  // (es fungible). `sellPrice: 0` ⇒ el vendor NO lo recompra (sink puro). El uso/
  // consumo del bond por un beneficio premium queda TBD (no inventar). Ver
  // docs/ECONOMY_EXCHANGE_MARKETPLACE.md §6.2. Ícono real pendiente en
  // client/assets/item/aureum_seal.png (32x32) → cae a noicon hasta entonces.
  bond_aureum_seal: {
    id: 'bond_aureum_seal',
    name: 'Sello de Aureum',
    sellable: true,
    icon: 'aureum_seal',
    type: ItemType.MATERIAL,
    description: 'Sello dorado acuñado por la Casa de Subastas. Moneda de prestigio, intercambiable.',
    maxStack: 1000,
    modifiers: {},
    buyPrice: 5000,
    sellPrice: 0,
  },
};

// ===========================================================================
// MINERÍA + LEÑERÍA: menas, picos, hachas de leñador y troncos.
// Generados en bloque (DRY) y asignados a ITEMS. Íconos/animaciones reales
// pendientes → 'noicon' + reuso del visual de hacha de bronce (weaponId 6).
// Los tiers de herramienta/mineral NO incluyen 'dragon' (no es minable/forjable).
// Ver docs/MINING_AND_SMITHING.md y docs/SKILLS_SYSTEM.md.
// ===========================================================================

/**
 * Tiers de mineral/herramienta ordenados de menor a mayor (sin dragón).
 * `obsidian` es el 7º tier y el **mejor** mineral forjable (arriba de Runita), para
 * dar parity con las 7 especies de árboles de Leñería. Dragón sigue siendo el tope
 * del gear pero NO forjable (ver docs/MINING_AND_SMITHING.md).
 */
export const TOOL_TIERS = [
  'iron',
  'steel',
  'mithril',
  'adamant',
  'rune',
  'obsidian',
] as const;
export type ToolTier = (typeof TOOL_TIERS)[number];

/** Nombre visible (español, sustantivo) por tier. */
export const TIER_DISPLAY_NAME: Record<ToolTier, string> = {
  iron: 'Hierro',
  steel: 'Acero',
  mithril: 'Mithril',
  adamant: 'Adamantita',
  rune: 'Runita',
  obsidian: 'Obsidiana',
};

/**
 * Nivel de Minería requerido por tier (para minar la mena y usar el pico).
 * Hierro es el PRIMER mineral (nivel 1); la curva sube parejo hasta Obsidiana (90),
 * pináculo que espeja el Pino de Leñería (nivel 90). El Bronce dejó de ser minable
 * (sólo sobrevive como gear inicial). El Carbón se mina al nivel del Acero (18) y
 * vive como nodo standalone en resourceNodes.ts (no es un tier).
 */
export const TIER_MINING_LEVEL: Record<ToolTier, number> = {
  iron: 1,
  steel: 18,
  mithril: 36,
  adamant: 54,
  rune: 72,
  obsidian: 90,
};

/** Nivel de Herrería requerido para FORJAR (comprar) cada pico/hacha de ese tier. */
const TIER_TOOL_SMITHING_LEVEL: Record<ToolTier, number> = {
  iron: 20,
  steel: 35,
  mithril: 55,
  adamant: 75,
  rune: 90,
  obsidian: 95,
};

/** Probabilidad de rotura por extracción (baja; menor cuanto mejor el material). */
const TIER_BREAK_CHANCE: Record<ToolTier, number> = {
  iron: 0.04,
  steel: 0.03,
  mithril: 0.02,
  adamant: 0.015,
  rune: 0.01,
  obsidian: 0.008,
};

/** Precio de venta al vendor de cada mena (piso económico). */
const TIER_ORE_SELL_PRICE: Record<ToolTier, number> = {
  iron: 5,
  steel: 12,
  mithril: 25,
  adamant: 45,
  rune: 80,
  obsidian: 110,
};

/** Precio de compra de picos/hachas por tier. */
const TIER_TOOL_BUY_PRICE: Record<ToolTier, number> = {
  iron: 100,
  steel: 250,
  mithril: 600,
  adamant: 1300,
  rune: 3000,
  obsidian: 5000,
};

/** Stats de combate flojos de la herramienta por tier (min/max daño). */
const TIER_TOOL_DAMAGE: Record<ToolTier, [number, number]> = {
  iron: [3, 6],
  steel: [4, 8],
  mithril: [5, 10],
  adamant: [6, 12],
  rune: [7, 14],
  obsidian: [8, 16],
};

/**
 * Carbón requerido para FORJAR cada tier (estilo RuneScape: cuanto mejor el tier,
 * más carbón). Hierro no lleva carbón; de Acero en adelante escala +2. Es DATO para
 * el futuro loop de crafteo (mena+carbón→lingote): hoy sólo se muestra en la
 * descripción del lingote (no hay receta que lo consuma todavía). El Carbón se
 * extrae de su propio yacimiento (ver resourceNodes.ts `ore_node_coal`).
 */
export const TIER_COAL_COST: Record<ToolTier, number> = {
  iron: 0,
  steel: 2,
  mithril: 4,
  adamant: 6,
  rune: 8,
  obsidian: 10,
};

/**
 * ── Receta de FUNDICIÓN mena+carbón → lingote (dato para el futuro loop de Herrería) ──
 * Cada lingote lleva **1 mena** del tier + `TIER_COAL_COST` de carbón, pide un **nivel de
 * Herrería** (`TIER_SMELT_LEVEL`) y otorga XP de Herrería (`TIER_SMELT_XP`) al fundir. Hoy
 * es METADATA (no hay acción de fundir todavía); se muestra en la wiki. Se enforza cuando
 * exista el crafteo. Curva estilo OSRS (fundir es más bajo que forjar el equipo).
 */
export const TIER_ORE_PER_BAR = 1;
export const TIER_SMELT_LEVEL: Record<ToolTier, number> = {
  iron: 1,
  steel: 15,
  mithril: 30,
  adamant: 50,
  rune: 70,
  obsidian: 90,
};
export const TIER_SMELT_XP: Record<ToolTier, number> = {
  iron: 12,
  steel: 18,
  mithril: 30,
  adamant: 40,
  rune: 52,
  obsidian: 65,
};

(function buildGatheringItems(): void {
  for (const tier of TOOL_TIERS) {
    const tierName = TIER_DISPLAY_NAME[tier];

    // Mena (output de minería) — material stackeable, droppeable.
    ITEMS[`material_ore_${tier}`] = {
      id: `material_ore_${tier}`,
      name: `Mena de ${tierName}`,
      sellable: true,
      // Ícono real pendiente en client/assets/item/ore_<tier>.png (32x32). Si no
      // existe, el cliente cae a noicon automáticamente (drop-in al agregar el PNG).
      icon: `ore_${tier}`,
      type: ItemType.MATERIAL,
      description: `Roca con mineral de ${tierName.toLowerCase()}. Se funde en herrería. Extraída con un pico.`,
      maxStack: 1000,
      materialTier: tier,
      modifiers: {},
      sellPrice: TIER_ORE_SELL_PRICE[tier],
    };

    // Pico de minero (equipable, funciona también desde inventario).
    ITEMS[`tool_pickaxe_${tier}`] = {
      id: `tool_pickaxe_${tier}`,
      name: `Pico de ${tierName}`,
      sellable: true,
      // Ícono real pendiente en client/assets/weapon/pickaxe_<tier>.png (32x32).
      // Cae a noicon hasta que exista (drop-in). El VISUAL equipado sigue usando
      // weaponId 6 (hacha de bronce) hasta tener spritesheet propio.
      icon: `pickaxe_${tier}`,
      type: ItemType.EQUIPMENT,
      subtype: WeaponSubtype.AXE,
      slot: 'weapon',
      weaponId: 6, // reusa visual de hacha de bronce hasta tener arte propio
      weaponHand: 'one',
      attackWidth: 1,
      attackLength: 1,
      attackStaminaCost: 6,
      description: `Pico de ${tierName.toLowerCase()} para extraer minerales. También sirve como arma improvisada.`,
      materialTier: tier,
      toolType: 'pickaxe',
      toolTier: tier,
      toolBreakChance: TIER_BREAK_CHANCE[tier],
      requiredMiningLevel: TIER_MINING_LEVEL[tier],
      requiredSmithingLevel: TIER_TOOL_SMITHING_LEVEL[tier],
      modifiers: {
        minDamage: TIER_TOOL_DAMAGE[tier][0],
        maxDamage: TIER_TOOL_DAMAGE[tier][1],
      },
      buyPrice: TIER_TOOL_BUY_PRICE[tier],
    };

    // Hacha de leñador (equipable). Loop de tala (árboles) es tanda futura;
    // el item ya existe para forjarlo/venderlo.
    ITEMS[`tool_hatchet_${tier}`] = {
      id: `tool_hatchet_${tier}`,
      name: `Hacha de Leñador de ${tierName}`,
      sellable: true,
      // Ícono real pendiente en client/assets/weapon/hatchet_<tier>.png (32x32).
      // Cae a noicon hasta que exista (drop-in).
      icon: `hatchet_${tier}`,
      type: ItemType.EQUIPMENT,
      subtype: WeaponSubtype.AXE,
      slot: 'weapon',
      weaponId: 6,
      weaponHand: 'one',
      attackWidth: 1,
      attackLength: 1,
      attackStaminaCost: 6,
      description: `Hacha de ${tierName.toLowerCase()} para talar árboles. También sirve como arma improvisada.`,
      materialTier: tier,
      toolType: 'hatchet',
      toolTier: tier,
      toolBreakChance: TIER_BREAK_CHANCE[tier],
      requiredWoodcuttingLevel: TIER_MINING_LEVEL[tier],
      requiredSmithingLevel: TIER_TOOL_SMITHING_LEVEL[tier],
      modifiers: {
        minDamage: TIER_TOOL_DAMAGE[tier][0],
        maxDamage: TIER_TOOL_DAMAGE[tier][1],
      },
      buyPrice: TIER_TOOL_BUY_PRICE[tier],
    };
  }

  // ── Picos ÚNICOS (skiller): cada uno lleva UN mod de minería (sistema de mods estándar,
  //    leído en GameRoom.tryGatherOnce). unique:true (LEGENDARY, mods fijos), equipables →
  //    aplican su mod SÓLO equipados (slot arma = uno a la vez, no apilan). toolTier mithril
  //    (minan hasta adamantita), gate de equip por Minería 36. Ícono placeholder
  //    (pickaxe_mithril) hasta arte propio; visual equipado = weaponId 6. Obtención
  //    (vendor/drop) pendiente: hoy sólo en la vitrina de debug. Ver docs/MINING_AND_SMITHING.md.
  const UPICK: ToolTier = 'mithril';
  const uniquePickBase = {
    sellable: true,
    icon: `pickaxe_${UPICK}`,
    type: ItemType.EQUIPMENT,
    subtype: WeaponSubtype.AXE,
    slot: 'weapon',
    weaponId: 6,
    weaponHand: 'one' as const,
    attackWidth: 1,
    attackLength: 1,
    attackStaminaCost: 6,
    unique: true,
    materialTier: UPICK,
    toolType: 'pickaxe' as const,
    toolTier: UPICK,
    toolBreakChance: TIER_BREAK_CHANCE[UPICK],
    requiredMiningLevel: TIER_MINING_LEVEL[UPICK],
    buyPrice: 25000,
  };
  const uPickDmg = { minDamage: TIER_TOOL_DAMAGE[UPICK][0], maxDamage: TIER_TOOL_DAMAGE[UPICK][1] };
  ITEMS['pickaxe_unique_gemologist'] = {
    ...uniquePickBase,
    id: 'pickaxe_unique_gemologist',
    name: 'Pico del Gemólogo',
    description:
      'Un pico de mithril con la punta engarzada en cuarzo. Hace aflorar gemas de la roca mucho más seguido.',
    modifiers: { ...uPickDmg, miningGemChanceBonus: 0.03 },
  };
  ITEMS['pickaxe_unique_prodigal'] = {
    ...uniquePickBase,
    id: 'pickaxe_unique_prodigal',
    name: 'Pico Pródigo',
    description:
      'Forjado por un enano avaro. Cada veta le rinde de más: a menudo arrancás una mena extra de un solo golpe.',
    modifiers: { ...uPickDmg, skillEffectiveness: { skill_mining: 0.5 } },
  };
  ITEMS['pickaxe_unique_richvein'] = {
    ...uniquePickBase,
    id: 'pickaxe_unique_richvein',
    name: 'Pico del Filón',
    description:
      'Su filo no agota la piedra: a veces extraés mineral sin que la veta se gaste, como si el filón fuera inagotable.',
    modifiers: { ...uPickDmg, miningNoDepleteChance: 0.15 },
  };

  // Maderas (output de leñería) — una por especie de árbol. Ícono real en
  // client/assets/item/wood_<species>.png (generado con gen_tinted_assets.py).
  // El árbol que la suelta y su nivel/respawn viven en shared/data/resourceNodes.ts.
  const WOODS: Array<{ id: string; name: string; icon: string; tree: string; sell: number }> = [
    { id: 'material_wood_maple', name: 'Madera de Arce', icon: 'wood_maple', tree: 'arce', sell: 2 },
    { id: 'material_wood_willow', name: 'Madera de Sauce', icon: 'wood_willow', tree: 'sauce', sell: 5 },
    { id: 'material_wood_oak', name: 'Madera de Roble', icon: 'wood_oak', tree: 'roble', sell: 9 },
    { id: 'material_wood_birch', name: 'Madera de Abedul', icon: 'wood_birch', tree: 'abedul', sell: 14 },
    { id: 'material_wood_palm', name: 'Madera de Palmera', icon: 'wood_palm', tree: 'palmera', sell: 20 },
    { id: 'material_wood_jacaranda', name: 'Madera de Jacarandá', icon: 'wood_jacaranda', tree: 'jacarandá', sell: 30 },
    { id: 'material_wood_pine', name: 'Madera de Pino', icon: 'wood_pine', tree: 'pino', sell: 42 },
  ];
  for (const wood of WOODS) {
    ITEMS[wood.id] = {
      id: wood.id,
      name: wood.name,
      sellable: true,
      icon: wood.icon,
      type: ItemType.MATERIAL,
      description: `${wood.name}. Materia prima de carpintería. Obtenida talando ${wood.tree} con un hacha de leñador.`,
      maxStack: 1000,
      modifiers: {},
      sellPrice: wood.sell,
    };
  }

  // Lingotes por tier (intermedio de herrería: mena → lingote → equipo; el crafteo
  // real es loop futuro). Ícono real en client/assets/item/ingot_<tier>.png (tinteado).
  // material_ingot_iron es además el fungible de prueba del Exchange.
  for (const tier of TOOL_TIERS) {
    const tierName = TIER_DISPLAY_NAME[tier];
    const coal = TIER_COAL_COST[tier];
    const coalNote = coal > 0 ? ` Su forja requiere ${coal} de carbón junto a la mena.` : '';
    ITEMS[`material_ingot_${tier}`] = {
      id: `material_ingot_${tier}`,
      name: `Lingote de ${tierName}`,
      sellable: true,
      icon: `ingot_${tier}`,
      type: ItemType.MATERIAL,
      description: `Barra de ${tierName.toLowerCase()} forjada. Insumo de herrería para forjar equipo.${coalNote}`,
      maxStack: 1000,
      modifiers: {},
      sellPrice: TIER_ORE_SELL_PRICE[tier] * 2,
    };
  }

  // Carbón: mineral standalone (NO es un tier — no tiene pico/lingote/colección propios).
  // Combustible de herrería que se funde junto a la mena para forjar del Acero en adelante
  // (cantidad = TIER_COAL_COST[tier]). Se extrae con pico de su propio yacimiento
  // (resourceNodes.ts `ore_node_coal`, nivel de Minería del Acero). Ícono real en
  // client/assets/item/coal.png (tinte oscuro, generado con gen_tinted_assets.py).
  ITEMS['material_coal'] = {
    id: 'material_coal',
    name: 'Carbón',
    sellable: true,
    icon: 'coal',
    type: ItemType.MATERIAL,
    description:
      'Carbón. Combustible de herrería: se funde junto a la mena para forjar del Acero en adelante (más carbón cuanto mejor el metal). Se extrae con un pico.',
    maxStack: 1000,
    modifiers: {},
    sellPrice: 15,
  };

  // Gemas (drop al minar, ver shared/data/gems.ts). Utilidad TBD; por ahora se
  // apilan y venden. Ícono real en client/assets/item/<icon>.png.
  for (const gem of GEMS) {
    ITEMS[gem.id] = {
      id: gem.id,
      name: gem.name,
      sellable: true,
      icon: gem.icon,
      type: ItemType.MATERIAL,
      description: `${gem.name}: gema que a veces aflora al picar minerales. Cuanto más rara, menos frecuente.`,
      maxStack: 1000,
      modifiers: {},
      buyPrice: gem.buy, // 1000+; la venta se deriva al 30% (normalización de precios)
    };
  }

  // Herramientas sueltas de crafteo/pesca: assets listos; sus loops (forja, carpintería,
  // sastrería, pesca) llegan después. Hoy son items visibles/vendibles.
  const MISC_TOOLS = [
    { id: 'tool_hammer', name: 'Martillo de Herrero', icon: 'tool_hammer', desc: 'Herramienta del Maestro Herrero. Forjar equipo es un loop futuro.', sell: 20 },
    { id: 'tool_saw', name: 'Sierra de Carpintero', icon: 'tool_saw', desc: 'Herramienta de carpintería. Craftear con madera es un loop futuro.', sell: 15 },
    { id: 'tool_scissors', name: 'Tijeras de Sastre', icon: 'tool_scissors', desc: 'Herramienta de sastrería. Confeccionar prendas es un loop futuro.', sell: 12 },
    { id: 'tool_fishing_rod', name: 'Caña de Pescar', icon: 'tool_fishing_rod', desc: 'Para pescar en aguas del mundo. La Pesca está en construcción.', sell: 25 },
    { id: 'tool_fishing_net', name: 'Red de Pesca', icon: 'tool_fishing_net', desc: 'Para pescar en cardúmenes. La Pesca está en construcción.', sell: 30 },
    { id: 'tool_fishing_fork', name: 'Arpón de Pesca', icon: 'tool_fishing_fork', desc: 'Para ensartar peces grandes. La Pesca está en construcción.', sell: 35 },
  ];
  for (const t of MISC_TOOLS) {
    ITEMS[t.id] = {
      id: t.id,
      name: t.name,
      sellable: true,
      icon: t.icon,
      type: ItemType.MATERIAL,
      description: t.desc,
      maxStack: 100,
      modifiers: {},
      sellPrice: t.sell,
    };
  }

  // ── PESCA: pescados (materia prima) + cañas por especie ──
  // Un pescado y una caña por pez (ver shared/data/fish.ts). El pescado es material de
  // cocina (comidas de pescado, tanda futura). La caña es la HERRAMIENTA que gatea su
  // cardumen (fish_node_<sp>.requiredToolItemId), crafteada en el banco de carpintería con
  // la madera 1:1 de la especie de árbol del escalón. Íconos reales pendientes (noicon).
  for (const f of FISH_SPECIES) {
    ITEMS[`material_fish_${f.species}`] = {
      id: `material_fish_${f.species}`,
      name: f.label,
      sellable: true,
      icon: `fish_${f.species}`, // ícono real pendiente → cae a noicon
      type: ItemType.MATERIAL,
      description: `${f.label} fresco. Se pesca en cardúmenes con la caña adecuada. Ingrediente de cocina.`,
      maxStack: 1000,
      modifiers: {},
      buyPrice: 20 + f.fishLevel * 3,
    };
    ITEMS[`tool_fishing_rod_${f.species}`] = {
      id: `tool_fishing_rod_${f.species}`,
      name: `Caña de ${f.label}`,
      sellable: true,
      icon: `fishing_rod_${f.species}`, // ícono real pendiente → cae a noicon
      type: ItemType.MATERIAL,
      description: `Caña de pescar para atrapar ${f.label.toLowerCase()}. Se craftea en el banco de carpintería con madera. Funciona guardada en el inventario.`,
      maxStack: 100,
      modifiers: {},
      toolType: 'fishing_rod',
      toolTier: f.tier,
      toolBreakChance: 0.01,
      buyPrice: 50 + f.fishLevel * 5,
    };
    // Pescado COCINADO (fogón, skill_cooking): comida "Bien Alimentado" de sustain, con
    // regeneración escalada por el escalón del pez (sardina flojo → atún fuerte). Ver
    // docs/COOKING_AND_FOOD.md.
    const regen = 1 + Math.floor(f.fishLevel / 20); // 1 (sardina) → 5 (atún)
    ITEMS[`food_cooked_${f.species}`] = {
      id: `food_cooked_${f.species}`,
      name: `${f.label} asado`,
      sellable: true,
      icon: `food_cooked_${f.species}`, // ícono real pendiente → cae a noicon
      type: ItemType.CONSUMABLE,
      description: `${f.label} cocinado al fuego. Al comerlo, "Bien Alimentado" acelera tu regeneración de vida, maná y stamina fuera de combate (15 min).`,
      maxStack: 20,
      consumeOnUse: true,
      buyPrice: 40 + f.fishLevel * 4,
      modifiers: {},
      foodBuff: {
        name: `Bien Alimentado: ${f.label} asado`,
        modifiers: { regenHP: regen, regenMana: regen, regenStamina: regen },
        durationMs: 15 * 60 * 1000,
      },
    };
  }

  // ── Partes de animal (drop raro de criaturas del Reino 'animal', para cocinar) ──
  // Se inyectan al loot de los NPCs animales en el roll de drop (GameRoom), no por lootTable
  // manual. Íconos reales pendientes (noicon). Ver docs/COOKING_AND_FOOD.md.
  ITEMS['material_raw_meat'] = {
    id: 'material_raw_meat',
    name: 'Carne cruda',
    sellable: true,
    icon: 'material_raw_meat',
    type: ItemType.MATERIAL,
    description: 'Un corte de carne de un animal. Ingrediente de cocina.',
    maxStack: 1000,
    modifiers: {},
    sellPrice: 8,
  };
  ITEMS['material_hide'] = {
    id: 'material_hide',
    name: 'Cuero',
    sellable: true,
    icon: 'material_hide',
    type: ItemType.MATERIAL,
    description: 'Piel curtible de un animal. Materia prima de artesanía (a futuro).',
    maxStack: 1000,
    modifiers: {},
    sellPrice: 6,
  };
})();

// ===========================================================================
// SASTRERÍA: tejidos (materia prima) + túnicas/armaduras de tela (output).
// Base de contenido de la skill `skill_tailoring` (procesamiento). El crafteo
// real (tejido → prenda) es tanda futura; hoy son items visibles/vendibles con
// su `requiredTailoringLevel` (metadata, igual que el ladder de Herrería). La
// armadura de tela es liviana: poca defensa física, buena defensa mágica (perfil
// de mago). Íconos reales pendientes → drop-in por nombre. Ver docs/SKILLS_SYSTEM.md.
// ===========================================================================

/** Tiers de tela ordenados de menor a mayor. */
export const CLOTH_TIERS = ['linen', 'wool', 'silk', 'mystic'] as const;
export type ClothTier = (typeof CLOTH_TIERS)[number];

export const CLOTH_TIER_NAME: Record<ClothTier, string> = {
  linen: 'Lino',
  wool: 'Lana',
  silk: 'Seda',
  mystic: 'Tela Mística',
};

/** Nivel de Sastrería requerido para confeccionar cada tier. */
export const CLOTH_TIER_TAILORING_LEVEL: Record<ClothTier, number> = {
  linen: 1,
  wool: 20,
  silk: 45,
  mystic: 70,
};

(function buildTailoringItems(): void {
  // Defensa por tier: [physDef, magicDef] — poca física, buena mágica.
  const DEF: Record<ClothTier, [number, number]> = {
    linen: [1, 2],
    wool: [1, 4],
    silk: [2, 6],
    mystic: [2, 9],
  };
  const CLOTH_SELL: Record<ClothTier, number> = { linen: 3, wool: 8, silk: 18, mystic: 40 };
  const ARMOR_PRICE: Record<ClothTier, number> = { linen: 45, wool: 90, silk: 180, mystic: 360 };
  const BODY_VISUAL: Record<ClothTier, number> = { linen: 1, wool: 2, silk: 3, mystic: 3 };

  CLOTH_TIERS.forEach((tier) => {
    const tierName = CLOTH_TIER_NAME[tier];

    // Tejido (materia prima de sastrería) — stackeable.
    ITEMS[`material_cloth_${tier}`] = {
      id: `material_cloth_${tier}`,
      name: `Tejido de ${tierName}`,
      sellable: true,
      // Ícono real pendiente en client/assets/item/cloth_<tier>.png (32x32). Cae a noicon.
      icon: `cloth_${tier}`,
      type: ItemType.MATERIAL,
      description: `Rollo de ${tierName.toLowerCase()}. Materia prima de sastrería para confeccionar túnicas y armaduras de tela.`,
      maxStack: 1000,
      materialTier: tier,
      modifiers: {},
      sellPrice: CLOTH_SELL[tier],
    };

    // Túnica de tela (armadura liviana, slot body). No affixable → siempre Común.
    ITEMS[`armor_cloth_${tier}`] = {
      id: `armor_cloth_${tier}`,
      name: `Túnica de ${tierName}`,
      sellable: true,
      // Ícono/anim reales pendientes → reusa el visual de vestimenta (bodyId) hasta
      // tener arte propio; el ícono cae a noicon.
      icon: 'noicon',
      type: ItemType.EQUIPMENT,
      // Tela (CLOTH): se integra al sistema de clases de armadura (gating por clase +
      // sólo clases mágicas la visten como tope). Ver shared/data/armorClasses.ts.
      subtype: ArmorSubtype.CLOTH,
      slot: 'body',
      bodyId: BODY_VISUAL[tier],
      materialTier: tier,
      requiredTailoringLevel: CLOTH_TIER_TAILORING_LEVEL[tier],
      description: `Túnica confeccionada en ${tierName.toLowerCase()}. Liviana: poca defensa física, buena defensa mágica.`,
      modifiers: {
        physDef: DEF[tier][0],
        magicDef: DEF[tier][1],
      },
      buyPrice: ARMOR_PRICE[tier],
    };
  });
})();

/**
 * ── Normalización de precios (regla global) ──
 * El **precio de compra** (`buyPrice`) es la fuente de verdad; la **venta** es el 30%
 * (`SELL_PRICE_RATIO`, vía getEffectiveSellPrice). Este pase corre una vez al cargar el
 * módulo y unifica todos los items:
 *  - Item con `keepSellPrice` → intacto (compra Y venta a mano, ej. únicos).
 *  - Item sólo con venta (materiales) → se le asigna `buyPrice = round(venta / 0.30)` y se
 *    borra el `sellPrice` explícito para que la venta derive al 30%.
 *  - Item con compra → se borra cualquier `sellPrice` explícito (deriva al 30%).
 * Así "la venta es siempre el 30% de la compra" salvo opt-out explícito.
 */
(function normalizePrices(): void {
  for (const item of Object.values(ITEMS)) {
    if (item.keepSellPrice) continue;
    const hasBuy = item.buyPrice !== undefined;
    const hasSell = item.sellPrice !== undefined;
    if (!hasBuy && !hasSell) continue;
    if (!hasBuy) item.buyPrice = Math.max(1, Math.round((item.sellPrice as number) / SELL_PRICE_RATIO));
    delete item.sellPrice; // la venta pasa a derivarse (30% de la compra) en getEffectiveSellPrice
  }
})();

/**
 * Bono de set que se activa al equipar una cantidad específica de piezas.
 * (Estructura preservada — el catálogo de SET_BONUSES está vacío post-wipe.)
 */
export interface SetBonus {
  pieces: number;
  modifiers: ItemModifiers;
  description?: string;
}

/**
 * Definición completa de un set de items.
 */
export interface SetDefinition {
  id: string;
  name: string;
  bonuses: SetBonus[];
}

/**
 * Bonos de set definidos para cada setId. Vacío post-wipe del 2026-05-25.
 * Cuando creemos nuevos sets, agregamos las entradas acá y referenciamos
 * `setId` desde los templates de items.
 */
export const SET_BONUSES: Record<string, SetDefinition> = {};

/**
 * Obtiene la definición de un set por su ID
 */
export function getSetDefinition(setId: string): SetDefinition | undefined {
  return SET_BONUSES[setId];
}

/**
 * Obtiene todos los bonos activos de un set basado en la cantidad de piezas equipadas.
 * Retorna los bonos que se activan con esa cantidad de piezas o menos (acumulativos).
 */
export function getActiveSetBonuses(setId: string, equippedPieces: number): SetBonus[] {
  const setDef = getSetDefinition(setId);
  if (!setDef) return [];
  return setDef.bonuses.filter((bonus) => equippedPieces >= bonus.pieces);
}

/**
 * Obtiene un template de item por ID
 */
export function getItemTemplate(itemId: string): ItemDefinition | undefined {
  return ITEMS[itemId];
}

/**
 * Obtiene todos los templates de items disponibles
 */
export function getAllItemTemplates(): ItemDefinition[] {
  return Object.values(ITEMS);
}

/**
 * Lookup reverso: dado el id de un hechizo, devuelve el item que lo OTORGA mientras esté
 * equipado (`grantsSpellId`), o undefined si ningún item lo otorga. Usado por la UI para
 * marcar un hechizo `grantOnly` como temporal y mostrar "Otorgado por &lt;item&gt;".
 */
export function getItemGrantingSpell(spellId: string): ItemDefinition | undefined {
  return Object.values(ITEMS).find((it) => it.grantsSpellId === spellId);
}

/**
 * F5 — Lookup reverso: dado un slot ('weapon'|'body'|'head'|'shield'|'boots')
 * y el visual asset id que viene del PlayerState (weaponId, bodyId, etc.),
 * devuelve el primer item template que matchea + tiene vfx.equipped.
 *
 * Si hay varios items con el mismo visual id, gana el primero — para los
 * casos de F5 (items con vfx son raros, generalmente unique/legendary), no
 * hay colisión esperada. Si hay ambigüedad real, evolucionar a enviar el
 * itemId equipado en el PlayerState.
 */
type EquipSlotKey = 'weapon' | 'body' | 'head' | 'shield' | 'boots';

export function getItemTemplateByVisualId(
  slot: EquipSlotKey,
  visualId: number
): ItemDefinition | undefined {
  if (!visualId) return undefined;
  const field: keyof ItemDefinition =
    slot === 'weapon' ? 'weaponId'
    : slot === 'body' ? 'bodyId'
    : slot === 'head' ? 'helmId'
    : slot === 'shield' ? 'shieldId'
    : 'bootId';
  for (const tpl of Object.values(ITEMS)) {
    if ((tpl as ItemDefinition)[field] === visualId && tpl.vfx?.equipped?.length) {
      return tpl;
    }
  }
  return undefined;
}

/**
 * Obtiene el nombre base de un item por ID
 */
export function getBaseItemName(itemId: string): string {
  const template = ITEMS[itemId];
  return template?.name ?? itemId;
}

/**
 * Deriva la rareza "nominal" de un template (sin rolear).
 * - unique → LEGENDARY
 * - setId → SET
 * - resto → COMMON (incluyendo affixable, cuyo verdadero valor se conoce sólo al rolear)
 *
 * Útil para showcases, listas de templates, y como fallback cuando no hay
 * instancia rolled disponible. Cuando hay instancia, preferir `rolledRarity`.
 */
export function deriveTemplateRarity(template: ItemDefinition | undefined): ItemRarity {
  if (!template) return ItemRarity.COMMON;
  if (template.unique) return ItemRarity.LEGENDARY;
  if (template.setId) return ItemRarity.SET;
  return ItemRarity.COMMON;
}
